// Channels and templates are backed by the real Go backend (backend) via
// the /api-auth proxy prefix - same server authApi.ts talks to, just a
// different path prefix for readability (see vite.config.ts).
//
// The backend's channel/template "list" endpoints only return nicknames (no
// loop construct exists server-side to hydrate every document into a single
// response - see backend/workflows/channel/list.wsl) - so listing here
// means one GET for the id list, then one GET per id, run in parallel.
//
// The backend document only stores a channel/template's own editable fields
// (title, description, isPublic, ..., and now workflows too - see
// mergeChannel's own comment). Chat-facing sub-resources (posts - messages
// and reports are both signals, see the "signals" section below and
// Message.type's REPORT_TYPE) and live counters (followers/following/likes)
// aren't implemented server-side - callers pass an `existing` object to
// merge functions so those fields survive a background refetch instead of
// being reset to empty every time.
import {
  uid,
  DEFAULT_MEMBER_PERMISSIONS,
  BUNCHING_INTERVALS,
  DEFAULT_BUNCHING_INTERVAL,
  type BunchingInterval,
  type Channel,
  type ChannelMemberPermissions,
  type ChannelRef,
  type Mapping,
  type SignalCalc,
  type MemberPermissions,
  type Template,
  type TemplateSignal,
  type Workflow,
} from '../types';
import { appLog } from '../lib/logger';
import { isFeatureStrict } from '../lib/features';
import type { ChartSettings } from '../components/charts/SignalActivityChart';

const BACKEND_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

function encodeNickname(nickname: string): string {
  return encodeURIComponent(nickname);
}

interface BackendResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  // Machine-readable failure code the backend attaches to some 4xx bodies
  // (currently only {code:"channel_limit"} from POST /channel when a
  // pricing-tier cap is hit - see backend/workflows/channel/create.wsl).
  code?: string;
  // The HTTP status of the response. The backend now maps a workflow's
  // declared error status onto the real HTTP status (see backend/README.md's
  // "HTTP error status codes") - 401 unauthenticated, 403 forbidden, 404
  // not found, 409 conflict, ... - so callers can branch on it instead of
  // string-matching `error`. Undefined only when the request never got a
  // response (network error).
  status?: number;
}

// Session-expiry hook. An authenticated request coming back HTTP 401 means
// the httpOnly zmist_session cookie is gone or no longer valid (it used to
// come back as a 200 with {data:{error:"...token"}}, indistinguishable from
// a normal not-found; the backend now returns a real 401 - see
// backend/README.md). StoreContext registers a handler here that clears
// local auth state so the app drops back to the login screen instead of
// erroring on every subsequent action. Kept module-level (not a param on
// every call) so all five api/*.ts files can report into one place.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// Called by every api/*.ts request helper with the response's HTTP status.
// Only a 401 does anything today; the switch keeps room for 419/440-style
// "session expired" codes without touching call sites.
export function reportHttpStatus(status: number): void {
  if (status === 401) unauthorizedHandler?.();
}

// Thrown by mutations that fail because the caller's pricing tier doesn't
// allow the action (HTTP 403 + a known `code`). The UI catches this to show
// <UpgradePrompt> instead of a generic error toast.
export class LimitError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'LimitError';
    this.code = code;
  }
}

// The standard envelope every paginated backend list endpoint now returns
// (see backend/workflows/channel/list.wsl and its siblings) -
// {data: [...], meta: {pagination: {...}}}, nested one level inside the
// {success, data, error} wrapper backendRequest above already unwraps, so
// callers here see it as BackendResult<Paginated<T>>.data.
export interface PaginationMeta {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface Paginated<T> {
  data: T[];
  meta: { pagination: PaginationMeta };
}

// Shared page-size default matching the backend's own default (see
// services/common/pagination.ResolvePageParams) - not load-bearing (an
// omitted limit already defaults server-side), just keeps client and
// server "20 per page" in one obvious place for anyone changing it.
export const DEFAULT_PAGE_SIZE = 20;

// The backend's session lives in an httpOnly zmist_session cookie now,
// invisible to JS by design (see StoreContext.tsx's bootstrap comment) -
// `credentials: 'include'` on every fetch below is what makes the browser
// actually send it (and, on /login|/register|/oauth/exchange's response,
// actually store the Set-Cookie the backend sends back).
//
// zmist_csrf is the deliberate exception: a second, non-httpOnly cookie
// made specifically so this code CAN read it, for the double-submit CSRF
// pattern every mutating (non-GET) authenticated request needs - see
// backend/modules/session/transitions/session.go's VerifyCsrf doc
// comment for the full rationale.
export function readCsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)zmist_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function backendRequest<T = any>(path: string, method: string, body?: unknown): Promise<BackendResult<T>> {
  try {
    const response = await fetch(`${BACKEND_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let parsed: any = null;
    try {
      parsed = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = parsed?.data;
    if (response.ok && data && typeof data === 'object' && !hasError(data)) {
      return { success: true, data: data as T, status: response.status };
    }
    const message = (data && typeof data === 'object' && data.error) || `Request failed (${response.status})`;
    const code = data && typeof data === 'object' && typeof data.code === 'string' ? data.code : undefined;
    return { success: false, error: String(message), code, status: response.status };
  } catch {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

// Same shape as backendRequest, but for routes that need a caller
// identity - the stats endpoints below (see backend/workflows/stats/
// channel_like.wsl's header comment on why: the old version was a plain
// public counter anyone could spam with no per-account tracking at all),
// profile updates, password changes, CSV import. No token to check or
// attach client-side any more - the cookie rides along automatically via
// credentials: 'include', and the backend answers 401 (surfaced through
// the same data.error handling below) if it's missing or invalid. Every
// non-GET call also needs the X-CSRF-Token header - see readCsrfCookie's
// comment.
// A workflow that always spells out its error field answers `error: null` on
// success (e.g. /mappings/ai-setup), so only a real, non-empty error counts.
function hasError(data: object): boolean {
  const e = (data as { error?: unknown }).error;
  return e !== undefined && e !== null && e !== '';
}

async function authorizedBackendRequest<T = any>(path: string, method: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<BackendResult<T>> {
  try {
    const headers: Record<string, string> = { ...extraHeaders };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') {
      const csrf = readCsrfCookie();
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }
    const response = await fetch(`${BACKEND_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let parsed: any = null;
    try {
      parsed = await response.json();
    } catch {
      // no JSON body
    }
    reportHttpStatus(response.status);
    const data = parsed?.data;
    if (response.ok && data && typeof data === 'object' && !hasError(data)) {
      return { success: true, data: data as T, status: response.status };
    }
    const message = (data && typeof data === 'object' && data.error) || `Request failed (${response.status})`;
    const code = data && typeof data === 'object' && typeof data.code === 'string' ? data.code : undefined;
    return { success: false, error: String(message), code, status: response.status };
  } catch {
    return { success: false, error: 'Could not reach the server. Is the backend running?' };
  }
}

// ---- channels ------------------------------------------------------------

function coerceBunchingInterval(value: unknown): BunchingInterval | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return BUNCHING_INTERVALS.includes(n as BunchingInterval) ? (n as BunchingInterval) : undefined;
}

// Workflows are now a real stored field on the channel doc (see backend/
// workflows/channel/create|update.wsl), but an empty array round-trips
// through this engine's RedisJSON layer as the literal STRING "[ ]", not
// a real [] - same class of quirk coerceBool's comment documents for
// booleans-as-strings (chartCompareChannels/tags hit this too; see
// coerceChartCompareChannels' identical Array.isArray guard). Without
// this, a freshly-synced channel's `workflows` was that string, and
// WorkflowListScreen's `c.workflows.map(...)` threw "workflows.map is not
// a function", blanking the whole screen (reproduced live while fixing
// this exact bug's "workflows don't persist" report - the save silently
// "worked" because the thrown render only happened on the NEXT load/sync,
// not immediately after Save).
function coerceWorkflows(value: unknown): Workflow[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value as Workflow[];
}

// See ChannelRef's comment (types.ts) - a denormalized display snapshot,
// not live references, so this only needs to guard against a malformed/
// missing array rather than re-hydrate anything.
function coerceChartCompareChannels(value: unknown): ChannelRef[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((v): v is Record<string, unknown> => v && typeof v === 'object')
    .map((v) => ({
      id: String(v.id ?? ''),
      title: String(v.title ?? ''),
      nickname: String(v.nickname ?? ''),
      accentHex: String(v.accentHex ?? '#3B82F6'),
    }))
    .filter((c) => c.id);
}

// Same shape/guard as coerceChartCompareChannels - reportAdditionalChannels
// is the same kind of denormalized ChannelRef[] snapshot, just for the
// Reports screen's Multi-Channel Report picker instead of the chart's.
const coerceReportAdditionalChannels = coerceChartCompareChannels;

function mergeChannel(data: any, existing?: Channel): Channel {
  return {
    id: data.id ?? data.nickname,
    title: data.title ?? '',
    nickname: data.nickname ?? '',
    description: data.description ?? '',
    templateIds: data.templateIds ?? [],
    viewType: data.viewType ?? existing?.viewType ?? 'reports',
    viewChartType: data.viewChartType ?? existing?.viewChartType ?? 'bar',
    viewDesign: data.viewDesign ?? existing?.viewDesign ?? 'classic',
    chartSettings: data.chartSettings ?? existing?.chartSettings ?? {},
    publicChartFiltersAllowed: coerceBool(data.publicChartFiltersAllowed) ?? existing?.publicChartFiltersAllowed ?? false,
    isPublic: coerceBool(data.isPublic) ?? false,
    // ownerIds is the array form; `data.ownerId` (scalar) is only seen from
    // a backend not yet migrated (see backend/cmd/zmist-cli
    // migrate-channel-owner-ids) - fold it in so the client is array-only.
    ownerIds: data.ownerIds ?? (data.ownerId ? [data.ownerId] : (existing?.ownerIds ?? [])),
    accentHex: data.accentHex ?? existing?.accentHex ?? '#3B82F6',
    avatarSymbol: data.avatarSymbol ?? existing?.avatarSymbol ?? 'message-circle',
    avatarImageKey: data.avatarImageKey ?? existing?.avatarImageKey ?? '',
    // Real counts (see GET /stats/channel/{nickname}) - `data.followers`/
    // `data.likes` are only present when the caller merged in a fresh
    // fetchChannelStats() result (see fetchChannel below); otherwise fall
    // back to whatever's cached. `liked`/`following` (this account's own
    // state, not a count) are never fetched here - only
    // ChannelDetailsScreen needs them, via a separate authenticated
    // fetchMyChannelStats() call, since fetching them for every channel in
    // a list would double the request count for no visible benefit there.
    followers: data.followers ?? existing?.followers ?? 0,
    likes: data.likes ?? existing?.likes ?? 0,
    liked: existing?.liked ?? false,
    following: existing?.following ?? false,
    muted: existing?.muted ?? false,
    posts: existing?.posts ?? [],
    // data.workflows is now a real server-stored field (see backend/
    // workflows/channel/create|update.wsl) - this used to always fall back
    // to `existing`, silently discarding whatever the server actually
    // returned, so a save only "looked" persisted (via the optimistic
    // local merge in StoreContext.patchChannel) until something else -
    // another fetchChannel, a reload - re-derived the channel from a fresh
    // server response and dropped it.
    workflows: coerceWorkflows(data.workflows) ?? existing?.workflows ?? [],
    tags: data.tags ?? existing?.tags ?? [],
    memberPermissions: parseMemberPermissions(data.memberPermissions, existing?.memberPermissions),
    revealRounds: coerceBool(data.revealRounds) ?? existing?.revealRounds ?? false,
    bunchingInterval: coerceBunchingInterval(data.bunchingInterval) ?? existing?.bunchingInterval ?? DEFAULT_BUNCHING_INTERVAL,
    chartCompareChannels: coerceChartCompareChannels(data.chartCompareChannels) ?? existing?.chartCompareChannels ?? [],
    reportAdditionalChannels:
      coerceReportAdditionalChannels(data.reportAdditionalChannels) ?? existing?.reportAdditionalChannels ?? [],
    priority: typeof data.priority === 'number' ? data.priority : Number(data.priority) || existing?.priority || 0,
    isDirect: coerceBool(data.isDirect) ?? existing?.isDirect ?? false,
  };
}

// Some older channel:profile docs in Redis store booleans as the strings
// "true"/"false" (e.g. @newchannel's revealRounds) - a raw `?? false`
// leaves the truthy string "false" in place, wrongly enabling round UI.
// Accept a real bool, "true"/"false", or 0/1; anything else is undefined.
// iOS Channel.swift's lenientBool mirrors this.
function coerceBool(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  return undefined;
}

// Normalises the roadmap-4a-f permission block off a wire payload (channel
// doc or member doc), falling back to a prior value then the permissive
// defaults - every flag always ends up a real boolean. textOnly wins if a
// stale payload somehow carries both restrictions on.
export function parseMemberPermissions(
  data: any,
  existing?: MemberPermissions
): MemberPermissions {
  const base = existing ?? DEFAULT_MEMBER_PERMISSIONS;
  const src = data ?? {};
  const textOnly = src.textOnly ?? base.textOnly;
  const signalsOnly = textOnly ? false : src.signalsOnly ?? base.signalsOnly;
  return {
    postMessages: src.postMessages ?? base.postMessages,
    uploadAttachments: src.uploadAttachments ?? base.uploadAttachments,
    importCsv: src.importCsv ?? base.importCsv,
    sendReports: src.sendReports ?? base.sendReports,
    textOnly,
    signalsOnly,
  };
}

export async function fetchChannel(nickname: string, existing?: Channel): Promise<Channel | null> {
  const [result, statsResult] = await Promise.all([
    backendRequest(`/channel/${encodeNickname(nickname)}`, 'GET'),
    backendRequest<ChannelStats>(`/stats/channel/${encodeNickname(nickname)}`, 'GET'),
  ]);
  if (!result.success || !result.data) return null;
  // Only merge stats in on a successful fetch - a transient failure here
  // (network hiccup, stats endpoint down) must fall through to whatever
  // `existing`/mergeChannel's default already has, not stomp a real cached
  // count with a wrong 0.
  const merged = statsResult.success && statsResult.data ? { ...result.data, ...statsResult.data } : result.data;
  return mergeChannel(merged, existing);
}

// ---- channel stats (likes/follows) ----------------------------------------
//
// Public counts (GET /stats/channel/{nickname}) vs. this account's own
// liked/following state (GET /stats/channel/{nickname}/me, Bearer-token
// protected) are deliberately separate backend calls - see
// backend/workflows/stats/channel_read.wsl vs. channel_read_me.wsl. Every
// write (like/unlike/follow/unfollow) requires login and is idempotent
// server-side (Redis Set membership, not a raw counter - see
// channel_like.wsl's header comment), so calling e.g. likeChannel() twice
// in a row is harmless and the response always reflects the resulting
// state, not "did this specific call do something."

export interface ChannelStats {
  likes: number;
  followers: number;
}

export async function fetchChannelStats(nickname: string): Promise<ChannelStats> {
  const result = await backendRequest<ChannelStats>(`/stats/channel/${encodeNickname(nickname)}`, 'GET');
  return result.success && result.data ? { likes: result.data.likes ?? 0, followers: result.data.followers ?? 0 } : { likes: 0, followers: 0 };
}

export interface ChannelMyStats {
  liked: boolean;
  following: boolean;
  muted: boolean;
}

// Returns the "logged out" default rather than throwing - every caller of
// this treats "don't know" the same as "no" (an anonymous visitor can't
// have liked/followed/muted anything), so there's no case where a caller
// needs to distinguish the two.
export async function fetchMyChannelStats(nickname: string): Promise<ChannelMyStats> {
  const result = await authorizedBackendRequest<ChannelMyStats>(`/stats/channel/${encodeNickname(nickname)}/me`, 'GET');
  return result.success && result.data
    ? { liked: !!result.data.liked, following: !!result.data.following, muted: !!result.data.muted }
    : { liked: false, following: false, muted: false };
}

// Live "watching now" + roll-up counts behind the channel stats button
// (GET /stats/channel/{n}/activity - owner/member/public gated server-side).
export interface ChannelActivity {
  messageCount: number;
  memberCount: number;
  pendingCount: number;
  viewersNow: number;
  watchingMembers: number;
  watchingGuests: number;
}

export async function fetchChannelActivity(nickname: string): Promise<ChannelActivity> {
  const result = await authorizedBackendRequest<ChannelActivity>(`/stats/channel/${encodeNickname(nickname)}/activity`, 'GET');
  const d = result.success && result.data ? result.data : null;
  return {
    messageCount: d?.messageCount ?? 0,
    memberCount: d?.memberCount ?? 0,
    pendingCount: d?.pendingCount ?? 0,
    viewersNow: d?.viewersNow ?? 0,
    watchingMembers: d?.watchingMembers ?? 0,
    watchingGuests: d?.watchingGuests ?? 0,
  };
}

// Server-side pre-aggregated chart rollups - GET /stats/channel/{n}/chart
// (backend internal/chartroll). Each series is one signal type; each slot is
// [t (unix s), count, sum, min, max, last, lastAt (ms), nval]. tier "m" is
// 1-minute slots (the Hours view), "q" 15-minute slots (everything else);
// fromSec 0 = the channel's whole history.
export interface ChartRollup {
  tier: 'm' | 'q';
  asOf: number;
  series: { type: string; slots: number[][] }[];
}

export async function fetchChartRollup(nickname: string, tier: 'm' | 'q', fromSec: number): Promise<ChartRollup | null> {
  const result = await authorizedBackendRequest<ChartRollup>(
    `/stats/channel/${encodeNickname(nickname)}/chart?tier=${tier}&from=${Math.max(0, Math.floor(fromSec))}`,
    'GET'
  );
  if (!result.success || !result.data) return null;
  return { tier, asOf: result.data.asOf ?? Date.now(), series: result.data.series ?? [] };
}

// Anomaly detection / correlations / predictions, derived server-side from
// the same chartroll rollups fetchChartRollup reads (backend
// internal/insight) - GET /stats/channel/{n}/insights. Everything here is
// computed fresh from the channel's actual numeric signal history: no
// invented alert thresholds, no fabricated confidence - a metric with too
// little history, a flat baseline, or a low-fit trend is simply absent
// from its list rather than shown with a made-up number.
export type InsightSeverity = 'high' | 'medium' | 'low';
export type InsightDirection = 'up' | 'down' | 'flat';

export interface InsightAnomaly {
  type: string;
  severity: InsightSeverity;
  value: number;
  baseline: number;
  stdDev: number;
  z: number;
  at: number; // unix seconds, the flagged slot's start
}

export interface InsightCorrelation {
  typeA: string;
  typeB: string;
  r: number;
  points: number;
  actionable: boolean;
}

export interface InsightPrediction {
  type: string;
  current: number;
  projected: number;
  direction: InsightDirection;
  confidence: number; // 0-100, the linear fit's R²
  horizonSlots: number;
}

export interface ChannelInsights {
  asOf: number;
  anomalies: InsightAnomaly[];
  correlations: InsightCorrelation[];
  predictions: InsightPrediction[];
}

export async function fetchChannelInsights(nickname: string): Promise<ChannelInsights> {
  const result = await authorizedBackendRequest<ChannelInsights>(`/stats/channel/${encodeNickname(nickname)}/insights`, 'GET');
  const d = result.success && result.data ? result.data : null;
  return {
    asOf: d?.asOf ?? Date.now(),
    anomalies: d?.anomalies ?? [],
    correlations: d?.correlations ?? [],
    predictions: d?.predictions ?? [],
  };
}

// The caller's unread signal count per channel (keyed by nickname) plus the
// cross-channel total - GET /stats/unread. Drives the channel-list row
// badges and the aggregate nav badge. Returns zeros rather than throwing:
// a badge that can't load should just not show, never break the list.
export interface UnreadCounts {
  byChannel: Record<string, number>;
  total: number;
  // Channel-list ordering inputs, folded into this same response (the
  // clients already call it on login + after every Cue relay): `pins` is
  // nickname -> pin score (negative; more negative = pinned more recently),
  // `opens` is nickname -> last-opened unix ms. Both feed sortChannels in
  // StoreContext.tsx. See backend/workflows/stats/unread.wsl.
  pins: Record<string, number>;
  opens: Record<string, number>;
}

export async function fetchUnreadCounts(): Promise<UnreadCounts> {
  const result = await authorizedBackendRequest<{ data?: Partial<UnreadCounts> }>(`/stats/unread`, 'GET');
  const d = result.success && result.data?.data ? result.data.data : null;
  return {
    byChannel: d?.byChannel ?? {},
    total: d?.total ?? 0,
    pins: d?.pins ?? {},
    opens: d?.opens ?? {},
  };
}

// POST/DELETE /channels/{n}/pin - pin or unpin a channel to the top of the
// caller's own list. Fire-and-forget style like markChannelRead; the store
// patches its local pin map optimistically and re-sorts.
export async function pinChannel(nickname: string): Promise<void> {
  await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/pin`, 'POST');
}

export async function unpinChannel(nickname: string): Promise<void> {
  await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/pin`, 'DELETE');
}

// POST/DELETE /channels/{n}/mute - mute or unmute push notifications for a
// channel, for the caller only (push:muted:<userId>, a Redis Set the push
// fan-out service already checks on every send - see channel/mute.wsl).
export async function muteChannel(nickname: string): Promise<void> {
  await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/mute`, 'POST');
}

export async function unmuteChannel(nickname: string): Promise<void> {
  await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/mute`, 'DELETE');
}

// POST /channels/{n}/read - mark the channel seen up to `signalId`, or up
// to its newest signal when omitted (what the clients send on open).
export async function markChannelRead(nickname: string, signalId?: string): Promise<void> {
  await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/read`, 'POST', signalId ? { signalId } : {});
}

export async function likeChannel(nickname: string): Promise<{ liked: boolean; likes: number }> {
  const result = await authorizedBackendRequest<{ liked: boolean; likes: number }>(`/stats/channel/${encodeNickname(nickname)}/like`, 'POST');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to like this channel.');
  return result.data;
}

export async function unlikeChannel(nickname: string): Promise<{ liked: boolean; likes: number }> {
  const result = await authorizedBackendRequest<{ liked: boolean; likes: number }>(`/stats/channel/${encodeNickname(nickname)}/unlike`, 'POST');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to unlike this channel.');
  return result.data;
}

// `profile` seeds the channel:member doc the backend now writes when the
// followed channel is public (status "accepted", so a follower can post
// signals - see backend/workflows/stats/channel_follow.wsl). Same
// caller-supplies-its-own-display-fields pattern as addChannelMember /
// requestJoinChannel; omitted for callers that don't have the store's
// profile handy (the member doc just gets empty display fields then).
export async function followChannel(
  nickname: string,
  profile?: { nickname: string; name: string; avatarSymbol: string }
): Promise<{ following: boolean; followers: number }> {
  const result = await authorizedBackendRequest<{ following: boolean; followers: number }>(
    `/stats/channel/${encodeNickname(nickname)}/follow`,
    'POST',
    profile
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to follow this channel.');
  return result.data;
}

export async function unfollowChannel(nickname: string): Promise<{ following: boolean; followers: number }> {
  const result = await authorizedBackendRequest<{ following: boolean; followers: number }>(`/stats/channel/${encodeNickname(nickname)}/unfollow`, 'POST');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to unfollow this channel.');
  return result.data;
}

// GET /me/following/feed (backend/workflows/stats/following_feed.wsl) - real
// updates from followed public channels, newest first. No "since last
// visit": the backend does not track last-seen for this feed.
export interface FollowingFeedItem {
  kind: 'report' | 'signals';
  at: number;
  channel: string;
  channelTitle: string;
  avatarSymbol: string;
  avatarImageKey?: string;
  accentHex: string;
  number?: number;
  excerpt?: string;
  signalType?: string;
  count?: number;
  last?: number;
  windowDays?: number;
}

export interface FollowingFeedPage {
  items: FollowingFeedItem[];
  followedCount: number;
  hasMore: boolean;
  nextCursor: number;
  windowDays: number;
}

export async function fetchFollowingFeed(limit = 20, cursor = 0): Promise<FollowingFeedPage> {
  const qs = `limit=${limit}${cursor > 0 ? `&cursor=${cursor}` : ''}`;
  const result = await authorizedBackendRequest<FollowingFeedPage>(`/me/following/feed?${qs}`, 'GET');
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Unable to load your feed.', result.status);
  return {
    items: result.data.items ?? [],
    followedCount: result.data.followedCount ?? 0,
    hasMore: !!result.data.hasMore,
    nextCursor: result.data.nextCursor ?? 0,
    windowDays: result.data.windowDays ?? 7,
  };
}

export interface ChannelsPage {
  channels: Channel[];
  pagination: PaginationMeta;
}

// `existing` (typically the caller's current state, which itself may have
// come from the localStorage cache - see StoreContext.tsx) lets a refetch
// re-attach local-only fields (chat history, live counters) by nickname
// instead of resetting them to empty, so a successful reconnect after an
// outage doesn't wipe out what the cache had preserved. `offset`/`limit`
// page through the real, standard pagination the backend now exposes (see
// backend/workflows/channel/list.wsl) - offset defaults to 0 (page 1),
// limit to DEFAULT_PAGE_SIZE, matching prior fixed-20-item behavior for
// any caller that doesn't pass either.
export async function fetchChannels(existing: Channel[] = [], offset = 0, limit = DEFAULT_PAGE_SIZE): Promise<ChannelsPage | null> {
  appLog(`[Sync] fetchChannels(): GET /channels?offset=${offset}&limit=${limit} starting`);
  // Routes through authorizedBackendRequest, not the plain backendRequest -
  // GET /channels now returns the caller's own channels (owned/joined),
  // not a global list, so it requires the same auth every other personal
  // endpoint does (see backend/workflows/channel/list.wsl).
  const index = await authorizedBackendRequest<Paginated<string>>(`/channels?offset=${offset}&limit=${limit}`, 'GET');
  if (!index.success || !index.data) {
    appLog('[Sync] fetchChannels(): GET /channels FAILED, leaving existing state untouched:', index.error);
    return null;
  }
  // The index can contain the same nickname more than once (LPush on every
  // create, no LREM to clean it up on delete/re-create - see
  // backend/workflows/channel/delete.wsl's comment), so dedupe before
  // hydrating each one into a full fetch.
  const nicknames = [...new Set(index.data.data ?? [])];
  const existingByNickname = new Map(existing?.map((c) => [c.nickname, c]));
  const channels = await Promise.all(nicknames?.map((n) => fetchChannel(n, existingByNickname.get(n))));
  const result = channels.filter((c): c is Channel => c !== null);
  appLog(`[Sync] fetchChannels(): index had ${nicknames.length} nickname(s), hydrated ${result.length}`, nicknames);
  return { channels: result, pagination: index.data.meta.pagination };
}

// Real, paginated prefix search over PUBLIC channel titles - unlike
// fetchChannels above (now the caller's own channels only, see its
// comment), this hits backend/workflows/channel/search.wsl, backed by a
// ZRANGEBYLEX sorted-set index maintained on create/update/delete - see
// searchTemplates' identical comment for why q/offset/limit are always
// sent even when empty, and why the {data, meta} envelope matters. No
// auth required - discovering a public channel you haven't joined yet
// shouldn't need a login.
export type ChannelSearchPage = Paginated<string>;

export async function searchChannels(query: string, offset = 0, limit = 50): Promise<ChannelSearchPage> {
  const qs = `?q=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}`;
  const result = await backendRequest<ChannelSearchPage>(`/channels/search${qs}`, 'GET');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to search channels.');
  return result.data;
}

// Routes through authorizedBackendRequest (cookie + CSRF), not the plain
// backendRequest above - create/update/delete all used to be unauthenticated
// at the route level, so the owner was whatever the request body claimed and
// literally anyone could edit or delete any channel with no login at all.
// Now that the backend derives ownerIds from the session and rejects a
// non-owner's update/delete with 403 (see backend/workflows/channel/
// create|update|delete.wsl), every call site needs a real identity attached.
// `cloneSource` is the only caller of the second param today - see
// cloneChannel below (roadmap #8 "click-source instrumentation").
// channel/create.wsl ignores any body field it doesn't recognize, so a
// plain create (no second arg) is byte-for-byte the same request as before.
export async function createChannel(draft: Channel, cloneSource?: string): Promise<Channel> {
  const body = cloneSource ? { ...draft, cloneSource } : draft;
  const result = await authorizedBackendRequest('/channel', 'POST', body);
  if (!result.success || !result.data) {
    if (result.code) throw new LimitError(result.error ?? 'Plan limit reached.', result.code);
    throw new Error(result.error ?? 'Unable to create channel.');
  }
  return mergeChannel(result.data, draft);
}

// A thrown request error that also carries the HTTP status, so the upsert
// wrappers below can tell a genuine 404 (the channel/template doesn't exist
// yet -> fall back to create) from a 403 "you don't own this" / 409 / 5xx,
// which must surface as-is. Retrying one of those as a create just produces
// a second, more confusing error ("a channel with that nickname already
// exists") and hides the real reason the save failed.
export class RequestError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
  }
}

export async function updateChannel(updated: Channel): Promise<Channel> {
  const result = await authorizedBackendRequest(`/channel/${encodeNickname(updated.nickname)}`, 'POST', updated);
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to update channel.', result.status);
  }
  return mergeChannel(result.data, updated);
}

export async function deleteChannel(nickname: string): Promise<void> {
  const result = await authorizedBackendRequest(`/channel/${encodeNickname(nickname)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to delete channel.');
}

// GET /channel/availability/{nickname} - already existed server-side but
// had no client caller until now. Public (no auth needed) - matches
// backend/workflows/channel/availability.wsl's own require list.
export async function checkChannelNicknameAvailable(nickname: string): Promise<boolean> {
  const result = await backendRequest<{ nickname: string; taken: boolean }>(
    `/channel/availability/${encodeNickname(nickname)}`,
    'GET'
  );
  return result.success && result.data ? !result.data.taken : false;
}

// POST /channel/rename/{nickname} - owner-only. Genuinely migrates the
// channel's Redis key families (profile, members, chart rollups, search
// index, cold storage, etc - see backend internal/rename) to the new
// nickname, unlike updateChannel above (which explicitly can't touch
// nickname - see channel/update.wsl's header comment).
export async function renameChannel(nickname: string, newNickname: string): Promise<string> {
  const result = await authorizedBackendRequest<{ nickname: string }>(
    `/channel/rename/${encodeNickname(nickname)}`,
    'POST',
    { nickname: newNickname }
  );
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to rename channel.', result.status);
  }
  return result.data.nickname;
}

// A draft's nickname might not exist on the backend yet - POST
// /channel/{nickname} 404s in that case, and only then does falling back to
// create make sense. A 403 (the caller isn't an owner - e.g. a non-owner
// who reached the edit form for a public channel), a 409, or a 5xx must
// propagate: retrying as a create would just mask "you don't own this
// channel" behind "a channel with that nickname already exists".
export async function upsertChannel(channel: Channel): Promise<Channel> {
  try {
    return await updateChannel(channel);
  } catch (err) {
    if (err instanceof RequestError && err.status === 404) {
      return await createChannel(channel);
    }
    throw err;
  }
}

// ---- templates -------------------------------------------------------

function mergeTemplate(data: any, existing?: Template): Template {
  return {
    id: data.id ?? data.nickname,
    title: data.title ?? '',
    nickname: data.nickname ?? '',
    isPublic: data.isPublic ?? false,
    colorHex: data.colorHex ?? existing?.colorHex ?? '#10B981',
    emoji: data.emoji ?? existing?.emoji ?? '✨',
    avatarImageKey: data.avatarImageKey ?? existing?.avatarImageKey ?? '',
    signals: data.signals ?? existing?.signals ?? [],
    // See mergeChannel's comment - real count when `data.likes` came from a
    // merged fetchTemplateStats() result (see fetchTemplate below),
    // otherwise whatever's cached.
    likes: data.likes ?? existing?.likes ?? 0,
    liked: existing?.liked ?? false,
    usages: existing?.usages ?? 0,
    ownerId: data.ownerId ?? existing?.ownerId ?? '',
  };
}

export async function fetchTemplate(nickname: string, existing?: Template): Promise<Template | null> {
  const [result, statsResult] = await Promise.all([
    backendRequest(`/template/${encodeNickname(nickname)}`, 'GET'),
    backendRequest<TemplateStats>(`/stats/template/${encodeNickname(nickname)}`, 'GET'),
  ]);
  if (!result.success || !result.data) return null;
  // See fetchChannel's comment - same "only merge on a successful fetch".
  const merged = statsResult.success && statsResult.data ? { ...result.data, ...statsResult.data } : result.data;
  return mergeTemplate(merged, existing);
}

// ---- template stats (likes) ------------------------------------------
//
// See the channel stats section above - same public-count-vs-authenticated
// -me split, same idempotent Redis-Set-backed like/unlike. Templates have
// no follow concept.

export interface TemplateStats {
  likes: number;
}

export async function fetchTemplateStats(nickname: string): Promise<TemplateStats> {
  const result = await backendRequest<TemplateStats>(`/stats/template/${encodeNickname(nickname)}`, 'GET');
  return result.success && result.data ? { likes: result.data.likes ?? 0 } : { likes: 0 };
}

export interface TemplateMyStats {
  liked: boolean;
}

export async function fetchMyTemplateStats(nickname: string): Promise<TemplateMyStats> {
  const result = await authorizedBackendRequest<TemplateMyStats>(`/stats/template/${encodeNickname(nickname)}/me`, 'GET');
  return result.success && result.data ? { liked: !!result.data.liked } : { liked: false };
}

// System-wide template roll-up - GET /stats/templates/summary (public).
// `byTemplate` is keyed by nickname; `channels` is how many channels list
// that template in their templateIds ("used in N channels" / "unused").
export interface TemplatesSummary {
  total: number;
  publicCount: number;
  privateCount: number;
  totalSignals: number;
  byTemplate: Record<string, { signals: number; channels: number }>;
}

// The Cue message pipeline's lifetime throughput per stage (Phase 5).
export interface PipelineStats {
  since: number; // unix seconds counting started, 0 if never
  received: number;
  triaged: number;
  persisted: number;
  broadcast: number;
  delivered: number; // individual per-recipient relay sends
  triageErrors: number;
  storageErrors: number;
  broadcastErrors: number;
}

// Platform-wide roll-up - GET /stats/system (authenticated).
export interface SystemStats {
  channels: number;
  signals: number;
  templates: number;
  users: number;
  pipeline: PipelineStats;
}

const emptyPipeline: PipelineStats = {
  since: 0,
  received: 0,
  triaged: 0,
  persisted: 0,
  broadcast: 0,
  delivered: 0,
  triageErrors: 0,
  storageErrors: 0,
  broadcastErrors: 0,
};

export async function fetchSystemStats(): Promise<SystemStats> {
  const result = await authorizedBackendRequest<{ data?: SystemStats }>(`/stats/system`, 'GET');
  const d = result.success && result.data?.data ? result.data.data : null;
  return {
    channels: d?.channels ?? 0,
    signals: d?.signals ?? 0,
    templates: d?.templates ?? 0,
    users: d?.users ?? 0,
    pipeline: { ...emptyPipeline, ...(d?.pipeline ?? {}) },
  };
}

// The caller's own footprint - GET /stats/me (authenticated).
export interface MyStats {
  ownedChannels: number;
  memberChannels: number;
  ownedTemplates: number;
  signalsInOwnedChannels: number;
  // This calendar month's stored-signal usage against the plan cap
  // (Phase 6). monthlySignalLimit: -1 unlimited, 0 = unresolved.
  monthlySignalsUsed: number;
  monthlySignalLimit: number;
}

export async function fetchMyStats(): Promise<MyStats> {
  const result = await authorizedBackendRequest<{ data?: MyStats }>(`/stats/me`, 'GET');
  const d = result.success && result.data?.data ? result.data.data : null;
  return {
    ownedChannels: d?.ownedChannels ?? 0,
    memberChannels: d?.memberChannels ?? 0,
    ownedTemplates: d?.ownedTemplates ?? 0,
    signalsInOwnedChannels: d?.signalsInOwnedChannels ?? 0,
    monthlySignalsUsed: d?.monthlySignalsUsed ?? 0,
    monthlySignalLimit: d?.monthlySignalLimit ?? 0,
  };
}

export async function fetchTemplatesSummary(): Promise<TemplatesSummary> {
  const result = await backendRequest<{ data?: TemplatesSummary }>(`/stats/templates/summary`, 'GET');
  const d = result.success && result.data?.data ? result.data.data : null;
  return {
    total: d?.total ?? 0,
    publicCount: d?.publicCount ?? 0,
    privateCount: d?.privateCount ?? 0,
    totalSignals: d?.totalSignals ?? 0,
    byTemplate: d?.byTemplate ?? {},
  };
}

export async function likeTemplate(nickname: string): Promise<{ liked: boolean; likes: number }> {
  const result = await authorizedBackendRequest<{ liked: boolean; likes: number }>(`/stats/template/${encodeNickname(nickname)}/like`, 'POST');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to like this schema.');
  return result.data;
}

export async function unlikeTemplate(nickname: string): Promise<{ liked: boolean; likes: number }> {
  const result = await authorizedBackendRequest<{ liked: boolean; likes: number }>(`/stats/template/${encodeNickname(nickname)}/unlike`, 'POST');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to unlike this schema.');
  return result.data;
}

export interface TemplatesPage {
  templates: Template[];
  pagination: PaginationMeta;
}

// See fetchChannels' comment on `existing`/`offset`/`limit` - same
// local-only-field preservation and real pagination. One request now: the
// backend hydrates each template document server-side with its like count
// folded in (see template/list.wsl / template/template.HydrateTemplates),
// so there's no per-nickname GET /template + GET /stats/template fan-out
// here anymore.
export async function fetchTemplates(existing: Template[] = [], offset = 0, limit = DEFAULT_PAGE_SIZE): Promise<TemplatesPage | null> {
  appLog(`[Sync] fetchTemplates(): GET /templates?offset=${offset}&limit=${limit} starting`);
  const page = await backendRequest<Paginated<any>>(`/templates?offset=${offset}&limit=${limit}`, 'GET');
  if (!page.success || !page.data) {
    appLog('[Sync] fetchTemplates(): GET /templates FAILED, leaving existing state untouched:', page.error);
    return null;
  }
  const existingByNickname = new Map(existing?.map((t) => [t.nickname, t]));
  // De-dupe by nickname (see fetchChannels' comment - same "no LREM"
  // duplicate-index possibility), first occurrence wins.
  const seen = new Set<string>();
  const result = (page.data.data ?? [])
    ?.filter((doc) => doc && typeof doc === 'object' && doc.nickname && !seen.has(doc.nickname) && seen.add(doc.nickname))
    ?.map((doc) => mergeTemplate(doc, existingByNickname.get(doc.nickname)));
  appLog(`[Sync] fetchTemplates(): hydrated ${result?.length}, ownerIds=${result?.map((t) => t.ownerId).join(', ')}`);
  return { templates: result, pagination: page.data.meta.pagination };
}

// Real, paginated prefix search over template titles - unlike
// fetchTemplates above (a fixed 20-item global recency window with no
// filtering), this hits backend/workflows/template/search.wsl (public)
// or search_mine.wsl (the caller's own, public+private), both backed by
// a ZRANGEBYLEX sorted-set index maintained on create/update/delete -
// safe to call against a 100k+-template index. Both endpoints require
// q/offset/limit present in the query string even when empty (the
// backend's argument binding fails on a fully-omitted param, same rule
// POST /signal's `id` follows) - this function always sends all three,
// so callers never need to think about that.
// {data: string[], meta: {pagination}} - the same envelope every other
// paginated list endpoint returns (see Paginated<T> above), not the
// {nicknames, offset, limit} shape this used to declare. That mismatch
// meant page.nicknames was always undefined, so TemplatePickerScreen's
// .map threw and its catch block silently reset results to [] on every
// search - the picker looked permanently empty rather than erroring.
export type TemplateSearchPage = Paginated<string>;

export async function searchTemplates(
  scope: 'public' | 'mine',
  query: string,
  offset = 0,
  limit = 50
): Promise<TemplateSearchPage> {
  const qs = `?q=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}`;
  const result =
    scope === 'mine'
      ? await authorizedBackendRequest<TemplateSearchPage>(`/templates/search/mine${qs}`, 'GET')
      : await backendRequest<TemplateSearchPage>(`/templates/search${qs}`, 'GET');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to search schemas.');
  return result.data;
}

// Same paginated prefix search as searchTemplates above, over mapping
// titles instead - backend/workflows/mapping/search.wsl (public) /
// search_mine.wsl (the caller's own, public+private).
export async function searchMappings(scope: 'public' | 'mine', query: string, offset = 0, limit = 50): Promise<TemplateSearchPage> {
  const qs = `?q=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}`;
  const result =
    scope === 'mine'
      ? await authorizedBackendRequest<TemplateSearchPage>(`/mappings/search/mine${qs}`, 'GET')
      : await backendRequest<TemplateSearchPage>(`/mappings/search${qs}`, 'GET');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to search mappings.');
  return result.data;
}

// Routes through authorizedBackendRequest (cookie + CSRF), not the plain
// backendRequest above - this route used to have no auth at all server-
// side, so anyone could create/edit/delete any template with no login at
// all (the same gap channel create/update/delete had before an earlier
// fix this session - see workflows/template/create|update|delete.wsl's
// identical comments). Only the template's own owner can update or
// delete it now.
// `cloneSource` - see createChannel's identical comment (roadmap #8
// "click-source instrumentation").
export async function createTemplate(draft: Template, cloneSource?: string): Promise<Template> {
  const body = cloneSource ? { ...draft, cloneSource } : draft;
  const result = await authorizedBackendRequest('/template', 'POST', body);
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to create schema.');
  return mergeTemplate(result.data, draft);
}

export async function updateTemplate(updated: Template): Promise<Template> {
  const result = await authorizedBackendRequest(`/template/${encodeNickname(updated.nickname)}`, 'POST', updated);
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to update schema.', result.status);
  }
  return mergeTemplate(result.data, updated);
}

// GET /template/availability/{nickname} - same shape as
// checkChannelNicknameAvailable above.
export async function checkTemplateNicknameAvailable(nickname: string): Promise<boolean> {
  const result = await backendRequest<{ nickname: string; taken: boolean }>(
    `/template/availability/${encodeNickname(nickname)}`,
    'GET'
  );
  return result.success && result.data ? !result.data.taken : false;
}

// POST /template/rename/{nickname} - owner-only. See renameChannel above;
// mirrors the same "migrate, don't just edit" distinction from
// updateTemplate (which can't touch nickname either).
export async function renameTemplate(nickname: string, newNickname: string): Promise<string> {
  const result = await authorizedBackendRequest<{ nickname: string }>(
    `/template/rename/${encodeNickname(nickname)}`,
    'POST',
    { nickname: newNickname }
  );
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to rename schema.', result.status);
  }
  return result.data.nickname;
}

// ---- CSV signal import -------------------------------------------------
//
// Both parsing and the mapping-to-signals conversion happen server-side
// (backend/modules/template/transitions/template.go's ParseCSVRows/
// ImportSignalsCSV) - real quoted-field CSV parsing lives in exactly one
// place, shared with the iOS app's identical flow
// (TemplateManageScreenView.CSVSignalImportSheet), instead of being
// reimplemented per client. This is a two-step, stateless flow: `parse`
// returns raw rows for a column-mapping UI to render, `commit` re-sends
// the same CSV text plus the chosen mapping and gets back the final
// signals array - there's no server-side storage for a pending upload, so
// the whole file goes over the wire again on commit (matches uploadAvatarImage's
// own resend-everything shape above).
//
// Both steps go through authorizedBackendRequest: the backend now requires
// a session on parse and commit alike (they store nothing, but an
// unauthenticated caller could POST arbitrary-size CSV for the server to
// parse in memory), and the X-CSRF-Token header it attaches is mandatory
// for these cookie-authed POSTs.

export interface CSVSignalMapping {
  name: string;
  value: string;
  colorHex: string;
  emoji: string;
  promptForValue: string;
}

// "auto" (the default) runs the backend's own delimiter detection;
// "comma"/"tab"/"space"/"semicolon"/"pipe"/a single custom character are
// explicit overrides for when auto-detection guesses wrong - see
// backend/modules/template/transitions/template.go's resolveDelimiter.
// quoted (default true) toggles RFC 4180 quote handling on/off - off is
// for a delimited export that never quotes fields and might have a
// literal quote character inside one (see readUnquotedRows's comment).
export interface CSVFormatOptions {
  delimiter: string;
  quoted: boolean;
}

export const CSV_DEFAULT_FORMAT: CSVFormatOptions = { delimiter: 'auto', quoted: true };

export async function parseSignalsCSV(csv: string, format: CSVFormatOptions = CSV_DEFAULT_FORMAT): Promise<string[][]> {
  const result = await authorizedBackendRequest<{ rows: string[][] }>('/template/import-signals/parse', 'POST', { csv, ...format });
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to parse that CSV file.');
  return result.data.rows;
}

interface ImportedSignalDTO {
  name: string;
  value?: string;
  colorHex: string;
  emoji: string;
  promptForValue: boolean;
}

export async function importSignalsCSV(
  csv: string,
  hasHeaderRow: boolean,
  mapping: CSVSignalMapping,
  format: CSVFormatOptions = CSV_DEFAULT_FORMAT
): Promise<{ signals: TemplateSignal[]; importedCount: number; skippedCount: number }> {
  const result = await authorizedBackendRequest<{ signals: ImportedSignalDTO[]; importedCount: number; skippedCount: number }>(
    '/template/import-signals/commit',
    'POST',
    { csv, hasHeaderRow, mapping, ...format }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to import that CSV file.');
  return {
    ...result.data,
    // The backend has no notion of a client-only id (see Channel/
    // TemplateDTO's identical rationale) - each imported signal needs one
    // minted locally before it can live in draft.signals.
    signals: result.data.signals.map((s) => ({ ...s, id: uid() })),
  };
}

// ---- CSV import for channel messages -----------------------------------
//
// Owner-only bulk import of CSV rows as chat messages ("signals" - see
// backend/workflows/signal/create.wsl's header comment) into a channel.
// Same two-step, stateless shape as the template signals import above:
// `parse` (authenticated but not channel-scoped - turning raw text into
// rows for a column-mapping UI carries no ownership implications on its
// own, but it still needs a session so it can't be used as an open
// CSV-parsing endpoint) then `commit` (authenticated + channel-scoped,
// re-sends the same CSV text plus the mapping). See
// backend/modules/signal/transitions/signal.go's
// ImportMessagesCSV for why the owner check and the actual per-row writes
// both happen there, in one pipelined call, instead of the client looping
// one POST /signal per row.

export interface CSVMessageMapping {
  sender: string;
  text: string;
  date: string;
  type: string;
  value: string;
}

export async function parseImportMessagesCSV(csv: string, format: CSVFormatOptions = CSV_DEFAULT_FORMAT): Promise<string[][]> {
  const result = await authorizedBackendRequest<{ rows: string[][] }>('/channel/messages/import/parse', 'POST', { csv, ...format });
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to parse that CSV file.');
  return result.data.rows;
}

export async function importMessagesCSV(
  channelNickname: string,
  csv: string,
  hasHeaderRow: boolean,
  mapping: CSVMessageMapping,
  format: CSVFormatOptions = CSV_DEFAULT_FORMAT,
  // A raw-Type-column-value -> desired-type lookup table, already
  // JSON.stringify'd by the caller (e.g. '{"server467":"Signal0"}') - see
  // backend/modules/signal/transitions/signal.go's ImportMessagesCSV for
  // why this crosses as one opaque string rather than a nested object:
  // WSL can only bind a fixed, known set of named arguments, and the set
  // of distinct raw values here is arbitrary and unbounded. Omit for "use
  // each row's raw Type-column value as-is" (the pre-existing behavior).
  typeValueMap?: string
): Promise<CSVImportResult> {
  const result = await authorizedBackendRequest<{ importedCount: number; skippedCount: number; skippedRows?: SkippedCSVRow[] }>(
    `/channel/${encodeNickname(channelNickname)}/messages/import/commit`,
    'POST',
    { csv, hasHeaderRow, mapping, ...format, ...(typeValueMap ? { typeValueMap } : {}) }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to import those messages.');
  return { ...result.data, skippedRows: result.data.skippedRows ?? [] };
}

// One row the importer declined to store, and why - drives
// CsvMessageImportSheet.tsx's "Download error report" link. `row` is the
// 1-indexed line number in the original file (header included, if any) -
// see backend/modules/signal/transitions/signal.go's ImportMessagesCSV.
export interface SkippedCSVRow {
  row: number;
  reason: string;
}

export interface CSVImportResult {
  importedCount: number;
  skippedCount: number;
  skippedRows: SkippedCSVRow[];
}

export async function deleteTemplate(nickname: string): Promise<void> {
  const result = await authorizedBackendRequest(`/template/${encodeNickname(nickname)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to delete schema.');
}

// See upsertChannel's comment - same "only a real 404 becomes a create"
// rule, so a non-owner's edit surfaces the 403 instead of a misleading
// "already exists".
export async function upsertTemplate(template: Template): Promise<Template> {
  try {
    return await updateTemplate(template);
  } catch (err) {
    if (err instanceof RequestError && err.status === 404) {
      return await createTemplate(template);
    }
    throw err;
  }
}

// ---- clone -----------------------------------------------------------
//
// There's no server-side clone endpoint - a clone is just a plain create
// carrying the source's content under a fresh, caller-owned identity. The
// backend keys both templates and channels by nickname and 409s on a
// collision (see workflows/template/create.wsl, channel/create.wsl,
// surfaced here as an "already exists" error), so this walks
// "<base>-copy", "<base>-copy-2", ... until a create lands. Identity and
// social-stat fields are dropped: create derives ownerId from the
// caller's own session, and every count/relationship starts fresh.

const CLONE_MAX_ATTEMPTS = 25;

function isNicknameConflict(err: unknown): boolean {
  return err instanceof Error && /already exists/i.test(err.message);
}

// "@acme-tasks" + attempt 1 -> {nickname: "@acme-tasks-copy", title: "Acme Tasks (copy)"}
// attempt 2 -> "@acme-tasks-copy-2" / "Acme Tasks (copy 2)". Re-cloning a
// clone strips the previous "-copy"/"(copy)" suffix first so names don't
// stack up as "...-copy-copy".
function cloneNames(baseNickname: string, baseTitle: string, attempt: number): { nickname: string; title: string } {
  const stem = baseNickname.replace(/^@/, '').replace(/-copy(-\d+)?$/, '') || 'item';
  const nickSuffix = attempt === 1 ? 'copy' : `copy-${attempt}`;
  const titleSuffix = attempt === 1 ? 'copy' : `copy ${attempt}`;
  const cleanTitle = baseTitle.replace(/\s*\(copy(?: \d+)?\)\s*$/, '').trim() || 'Untitled';
  return { nickname: `@${stem}-${nickSuffix}`, title: `${cleanTitle} (${titleSuffix})` };
}

// `clickSource` - which screen's Clone button drove this (roadmap #8
// "click-source instrumentation", see docs/CLAUDE_ZMIST_ROADMAP.md) -
// threads through to createTemplate/createChannel's cloneSource, logged +
// counted by channel/create.wsl / template/create.wsl only when present.
// Optional so a caller that doesn't care (or hasn't been updated) behaves
// exactly as before.
export async function cloneTemplate(source: Template, clickSource?: string): Promise<Template> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= CLONE_MAX_ATTEMPTS; attempt++) {
    const { nickname, title } = cloneNames(source.nickname, source.title, attempt);
    const draft: Template = {
      ...source,
      id: '',
      ownerId: '',
      nickname,
      title,
      // A clone starts private - the copier re-publishes it themselves
      // from the edit screen if they want to.
      isPublic: false,
      likes: 0,
      liked: false,
      usages: 0,
      // Drop the source's server-assigned global signal numbers -
      // create.wsl mints fresh ones for this new template.
      signals: source.signals.map((s) => ({ ...s, number: undefined })),
    };
    try {
      return await createTemplate(draft, clickSource);
    } catch (err) {
      if (!isNicknameConflict(err)) throw err;
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Unable to clone this schema.');
}

// Create-similar copies layout only: drop anything credential-like that the
// spread of `source` may have carried (ingest/API/share tokens, secrets, keys).
const CREDENTIAL_KEY = /token|secret|password|api_?key|credential/i;
function stripCredentialFields(obj: Record<string, unknown>): void {
  for (const k of Object.keys(obj)) if (CREDENTIAL_KEY.test(k)) delete obj[k];
}

// Studio v2 (flag studio.v2): the server-side clone, POST /channels/{nickname}/clone
// (backend/workflows/channel/clone.wsl). The server copies structure and view
// settings only, enforces plan limits and returns the new PRIVATE channel.
// One Idempotency-Key per in-flight source, so a double-click or a retried
// request replays the first clone instead of making a second channel.
const cloneInFlight = new Map<string, string>();

async function cloneChannelOnServer(source: Channel): Promise<Channel> {
  let key = cloneInFlight.get(source.nickname);
  if (!key) {
    key = crypto.randomUUID();
    cloneInFlight.set(source.nickname, key);
  }
  try {
    const result = await authorizedBackendRequest<{ channel: unknown }>(
      `/channels/${encodeNickname(source.nickname)}/clone`,
      'POST',
      {},
      { 'Idempotency-Key': key }
    );
    if (!result.success || !result.data?.channel) {
      if (result.code === 'channel_limit') throw new LimitError(result.error ?? 'Plan limit reached.', result.code);
      throw new RequestError(result.error ?? 'Unable to clone this channel.', result.status);
    }
    return mergeChannel(result.data.channel);
  } finally {
    cloneInFlight.delete(source.nickname);
  }
}

export async function cloneChannel(source: Channel, clickSource?: string): Promise<Channel> {
  if (isFeatureStrict('studio.v2')) return cloneChannelOnServer(source);
  let lastErr: unknown;
  for (let attempt = 1; attempt <= CLONE_MAX_ATTEMPTS; attempt++) {
    const { nickname, title } = cloneNames(source.nickname, source.title, attempt);
    const draft: Channel = {
      ...source,
      id: '',
      ownerIds: [],
      nickname,
      title,
      // create.wsl forces isPublic:false server-side anyway - mirrored
      // here so the locally-held draft never disagrees.
      isPublic: false,
      followers: 0,
      likes: 0,
      liked: false,
      following: false,
      posts: [],
      workflows: [],
    };
    stripCredentialFields(draft as unknown as Record<string, unknown>);
    try {
      return await createChannel(draft, clickSource);
    } catch (err) {
      if (!isNicknameConflict(err)) throw err;
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Unable to clone this channel.');
}

// "Publish as template" (roadmap #8) - the reverse of cloneChannel: instead
// of copying a channel's structure into a new channel, snapshot it into a
// new, standalone, owner-private Template - explicitly NOT a copy of the
// channel's messages/signals data or its members, just a reusable signal-
// preset palette (docs/CLAUDE_ZMIST_ROADMAP.md's own scope line: "strips
// signals data + members, keeps structure"). A channel has no signal list
// of its own - only `templateIds` (attached templates) - so `signals` here
// is the caller-resolved union of those attached templates' own signals
// (ChannelManageScreen.tsx already computes exactly this shape for the CSV
// importer's `availableSignals` prop; this reuses the same derivation),
// deduped by name so two attached templates that both define e.g. "Deploy"
// don't produce duplicate presets in the new one.
function dedupeSignalsByName(signals: TemplateSignal[]): TemplateSignal[] {
  const seen = new Set<string>();
  const out: TemplateSignal[] = [];
  for (const s of signals) {
    const key = s.name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    // Drop the source signal's global number - create.wsl mints a fresh
    // one for this new template, same as cloneTemplate does.
    out.push({ ...s, number: undefined });
  }
  return out;
}

export async function publishChannelAsTemplate(channel: Channel, signals: TemplateSignal[]): Promise<Template> {
  const stem = channel.nickname.replace(/^@/, '') || 'channel';
  const dedupedSignals = dedupeSignalsByName(signals);
  let lastErr: unknown;
  for (let attempt = 1; attempt <= CLONE_MAX_ATTEMPTS; attempt++) {
    const suffix = attempt === 1 ? 'template' : `template-${attempt}`;
    const draft: Template = {
      id: '',
      title: `${channel.title} Schema`,
      nickname: `@${stem}-${suffix}`,
      // Starts private, same as cloneChannel/cloneTemplate - the owner
      // re-publishes it themselves from the edit screen if they want it
      // listed publicly.
      isPublic: false,
      colorHex: channel.accentHex,
      emoji: '✨',
      avatarImageKey: channel.avatarImageKey,
      signals: dedupedSignals,
      likes: 0,
      liked: false,
      usages: 0,
      ownerId: '',
    };
    try {
      return await createTemplate(draft);
    } catch (err) {
      if (!isNicknameConflict(err)) throw err;
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Unable to publish this channel as a schema.');
}

// ---- integration mappings ---------------------------------------------
//
// Backed by backend/workflows/mapping/* - CRUD + list + search, the same
// shape as templates (nickname-keyed, public/private, owner-scoped). The
// `spec` object is opaque: stored and returned verbatim, edited as raw
// JSON in the UI. GET /mappings returns the caller's own mappings only.

function mergeMapping(data: any, existing?: Mapping): Mapping {
  return {
    id: data.id ?? data.nickname,
    nickname: data.nickname ?? '',
    title: data.title ?? '',
    description: data.description ?? existing?.description ?? '',
    isPublic: data.isPublic ?? existing?.isPublic ?? false,
    sourceType: data.sourceType ?? existing?.sourceType ?? '',
    spec: (data.spec && typeof data.spec === 'object' ? data.spec : existing?.spec) ?? {},
    ownerId: data.ownerId ?? existing?.ownerId ?? '',
    ingestTokenSet: data.ingestTokenSet ?? existing?.ingestTokenSet ?? false,
    // Absent on a mapping stored before this field existed - reads as
    // enabled, same tolerance the backend itself uses (see
    // mapping.SetEnabled's header comment).
    enabled: data.enabled ?? existing?.enabled ?? true,
    // Deliberately NOT falling back to existing?.ingestToken - a raw
    // token is only ever present in the response that just minted it
    // (see Mapping.ingestToken's comment); carrying a stale one forward
    // from a previous merge would make a since-rotated token look valid.
    ingestToken: data.ingestToken,
    // webhookSecret IS carried forward from `existing` (unlike
    // ingestToken) - every GET/POST response includes the current value
    // for the owner, not just a one-time mint/rotate response, so a
    // background refetch shouldn't make it disappear (see
    // Mapping.webhookSecret's comment).
    webhookSecret: data.webhookSecret ?? existing?.webhookSecret,
  };
}

export interface MappingsPage {
  mappings: Mapping[];
  pagination: PaginationMeta;
}

// See fetchTemplates - same `existing`/offset/limit contract. Owner-scoped
// (GET /mappings returns only the caller's own).
export async function fetchMappings(existing: Mapping[] = [], offset = 0, limit = DEFAULT_PAGE_SIZE): Promise<MappingsPage | null> {
  const page = await authorizedBackendRequest<Paginated<any>>(`/mappings?offset=${offset}&limit=${limit}`, 'GET');
  if (!page.success || !page.data) return null;
  const existingByNickname = new Map(existing.map((m) => [m.nickname, m] as const));
  const mappings = (page.data.data ?? []).map((doc: any) => mergeMapping(doc, existingByNickname.get(doc.nickname)));
  return { mappings, pagination: page.data.meta.pagination };
}

export async function fetchMapping(nickname: string, existing?: Mapping): Promise<Mapping | null> {
  const result = await authorizedBackendRequest(`/mapping/${encodeNickname(nickname)}`, 'GET');
  if (!result.success || !result.data) return null;
  return mergeMapping(result.data, existing);
}

export async function createMapping(draft: Mapping): Promise<Mapping> {
  const result = await authorizedBackendRequest('/mapping', 'POST', {
    nickname: draft.nickname,
    title: draft.title,
    description: draft.description,
    isPublic: draft.isPublic,
    sourceType: draft.sourceType,
    spec: draft.spec,
  });
  // {code:"connector_limit"} - a `sourceType:"webhook"` mapping past the
  // caller's tier cap (see backend/workflows/mapping/create.wsl's
  // CheckConnectorQuota chain). The screen catches this as LimitError to
  // show <UpgradePrompt> instead of a generic error toast.
  if (result.code) throw new LimitError(result.error ?? 'Plan limit reached.', result.code);
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Unable to create mapping.', result.status);
  return mergeMapping(result.data, draft);
}

export async function updateMapping(updated: Mapping): Promise<Mapping> {
  const result = await authorizedBackendRequest(`/mapping/${encodeNickname(updated.nickname)}`, 'POST', {
    title: updated.title,
    description: updated.description,
    isPublic: updated.isPublic,
    sourceType: updated.sourceType,
    spec: updated.spec,
  });
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Unable to update mapping.', result.status);
  return mergeMapping(result.data, updated);
}

export async function deleteMapping(nickname: string): Promise<void> {
  const result = await authorizedBackendRequest(`/mapping/${encodeNickname(nickname)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to delete mapping.');
}

// Mapping memory (docs/AUTOMATIC_SEMANTIC_SIGNAL_MAPPING.md's "Mapping
// Memory" - backend/internal/mappingmemory): a historical-match bias, per
// field name, drawn from this account's own previously-confirmed mapping
// decisions. Both calls are best-effort - a network hiccup here should
// never block generating or saving a mapping, only mean "no bias signal
// this time," so neither throws.
export interface MemorySuggestion {
  to: string;
  cast?: string;
  unitScale?: number;
  timestamp?: string;
  score: number;
}

export async function suggestMappingFieldsFromMemory(fields: string[]): Promise<Record<string, MemorySuggestion>> {
  if (fields.length === 0) return {};
  try {
    const result = await authorizedBackendRequest<{ suggestions: Record<string, MemorySuggestion> }>('/mappings/memory/suggest', 'POST', { fields });
    return result.success && result.data ? (result.data.suggestions ?? {}) : {};
  } catch {
    return {};
  }
}

export interface FieldDecision {
  name: string;
  to: string;
  cast?: string;
  unitScale?: number;
  timestamp?: string;
}

export async function recordMappingFieldDecisions(fields: FieldDecision[]): Promise<void> {
  if (fields.length === 0) return;
  try {
    await authorizedBackendRequest('/mappings/memory/record', 'POST', { fields });
  } catch {
    /* best-effort - a saved mapping must not fail over this */
  }
}

// LLM-assisted mapping suggestions (Phase 4 - backend/internal/
// mappingaisuggest): only for the handful of fields the caller already
// scored low-confidence, never applied automatically - the response fills
// the same candidate-pick dropdown a low-confidence field already shows.
export interface AIFieldSuggestion {
  to: string;
  cast?: string;
  unitScale?: number;
  timestamp?: string;
}

export interface AISuggestResult {
  suggestions: Record<string, AIFieldSuggestion>;
  error?: string;
}

export async function suggestMappingFieldsFromAI(provider: string, apiKey: string, fields: string[], sample: unknown): Promise<AISuggestResult> {
  if (fields.length === 0) return { suggestions: {} };
  try {
    const result = await authorizedBackendRequest<AISuggestResult>('/mappings/ai-suggest', 'POST', { provider, apiKey: apiKey || undefined, fields, sample });
    if (result.success && result.data) return result.data;
    return { suggestions: {}, error: result.error ?? 'Unable to reach the AI provider.' };
  } catch {
    return { suggestions: {}, error: 'Unable to reach the AI provider.' };
  }
}

// Whole-mapping plan from the LLM (POST /mappings/ai-plan - backend/internal/
// mappingaisuggest/plan.go). The server already validated it against the field
// paths we sent; the caller still previews it on the user's own sample.
export interface AIPlanRule {
  to: string;
  from?: string;
  const?: string;
  cast?: string;
  unitScale?: number;
  timestamp?: string;
}
export interface AIPlanCalc {
  type: string;
  unit?: string;
  unitPath?: string;
  period?: { startPath?: string; endPath?: string; framePath?: string };
}
export interface AIPlan {
  title: string;
  // How to present the value (currency/percentage/count/duration + unit + measured period);
  // becomes the mapping's `calc` block - see lib/aiPlan.ts planToSpec.
  calc?: AIPlanCalc;
  description?: string;
  recordPath?: string;
  rules: AIPlanRule[];
  notes?: string[];
}

// "Fetch data" in the manual Scraper wizard (POST /mappings/fetch-sample): Zmist
// GETs the source URL (the browser can't - CORS) and returns an example to map
// field -> field. A JSON document is reduced to its list of records; `recordPath`
// is that list's path (the saved mapping must carry it) and `candidates` every
// list found, so the user can pick another.
export interface FetchedSample {
  text: string;
  format: string; // 'json', or 'auto' when the body wasn't JSON
  bytes: number;
  recordPath: string;
  candidates: { path: string; len: number }[];
}

export async function fetchSampleFromUrl(url: string, recordPath?: string): Promise<{ sample?: FetchedSample; error?: string }> {
  try {
    const res = await authorizedBackendRequest<(Partial<FetchedSample> & { error?: string }) | null>('/mappings/fetch-sample', 'POST', {
      url,
      recordPath: recordPath || undefined,
    });
    const d = res.data;
    if (res.success && d && typeof d.text === 'string') {
      return { sample: { text: d.text, format: d.format ?? 'json', bytes: d.bytes ?? d.text.length, recordPath: d.recordPath ?? '', candidates: d.candidates ?? [] } };
    }
    return { error: d?.error ?? res.error ?? 'Could not fetch that URL.' };
  } catch {
    return { error: 'Could not fetch that URL.' };
  }
}

// The ONE call behind the AI integration form (POST /mappings/ai-setup).
// Records parsed in the browser go as fields+sample (the model only plans the
// rules); anything else goes as `text`, a description the server resolves into
// data source URL(s) it samples itself (public https only). Either way the
// answer is a validated plan the caller previews before saving.
export type AISetupResult =
  | { mode: 'webhook'; plan: AIPlan }
  | {
      mode: 'scrape';
      plan: AIPlan;
      source: { format: string; intervalSec: number; hint?: string };
      targets: { url: string; label?: string }[];
      skipped?: { label?: string; url: string; reason: string }[];
      records: unknown[];
      // An "all metrics" document: every list shaped like the one the plan was
      // made from. Absent/empty for an ordinary single-list source.
      metrics?: AIMetric[];
      metricsSkipped?: number;
    };

export interface AIMetric {
  path: string; // dotted path of the metric's record list in the document
  name: string; // Signal name
  unit?: string; // what the lists differ by in their last path segment (USD, shares...)
}

export async function aiSetup(
  provider: string,
  apiKey: string,
  input: { text: string } | { fields: string[]; sample: unknown },
): Promise<{ result?: AISetupResult; error?: string }> {
  try {
    const res = await authorizedBackendRequest<(Partial<AISetupResult> & { error?: string }) | null>('/mappings/ai-setup', 'POST', {
      provider,
      apiKey: apiKey || undefined,
      ...input,
    });
    const d = res.data;
    if (res.success && d?.plan && (d.mode === 'webhook' || (d.mode === 'scrape' && Array.isArray(d.targets) && Array.isArray(d.records)))) {
      return { result: d as AISetupResult };
    }
    return { error: d?.error ?? res.error ?? 'The AI could not set this up.' };
  } catch {
    return { error: 'Unable to reach the AI provider.' };
  }
}

// Mints a new ingest token, invalidating the old one - owner only. The
// response's ingestToken (see Mapping.ingestToken's comment) is the ONLY
// place the new raw token is ever shown; the caller must display/let the
// user copy it immediately.
export async function rotateMappingIngestToken(nickname: string, existing?: Mapping): Promise<Mapping> {
  const result = await authorizedBackendRequest(`/mappings/${encodeNickname(nickname)}/rotate-ingest-token`, 'POST');
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to rotate the ingest token.', result.status);
  }
  return mergeMapping(result.data, existing);
}

// Mints a new webhook HMAC secret (github/stripe-style connectors),
// invalidating the old one - owner only. Unlike rotateMappingIngestToken
// this isn't a one-time reveal (see Mapping.webhookSecret's comment):
// mergeMapping picks the new value up from the response like any other
// field, and it keeps showing up on later reads too.
export async function rotateMappingWebhookSecret(nickname: string, existing?: Mapping): Promise<Mapping> {
  const result = await authorizedBackendRequest(`/mappings/${encodeNickname(nickname)}/rotate-webhook-secret`, 'POST');
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? 'Unable to rotate the webhook secret.', result.status);
  }
  return mergeMapping(result.data, existing);
}

// Pauses or resumes a mapping - owner only. While paused, every
// server-controlled ingest path (webhook, Prometheus remote_write) rejects
// incoming data instead of writing Signals (see backend/internal/
// webhookrecv and internal/promwrite); an agent-pulled source isn't
// server-enforced the same way.
export async function setMappingEnabled(nickname: string, enabled: boolean, existing?: Mapping): Promise<Mapping> {
  const result = await authorizedBackendRequest(`/mappings/${encodeNickname(nickname)}/enabled`, 'POST', { enabled });
  if (!result.success || !result.data) {
    throw new RequestError(result.error ?? `Unable to ${enabled ? 'resume' : 'pause'} this integration.`, result.status);
  }
  return mergeMapping(result.data, existing);
}

// See upsertTemplate - only a real 404 becomes a create.
export async function upsertMapping(mapping: Mapping): Promise<Mapping> {
  try {
    return await updateMapping(mapping);
  } catch (err) {
    if (err instanceof RequestError && err.status === 404) {
      return await createMapping(mapping);
    }
    throw err;
  }
}

// ---- signals ("send message") -------------------------------------------
//
// There's no separate chat/message endpoint on the backend - a "signal" is
// how a message gets written into a channel (see
// backend/workflows/signal/create.wsl's header comment). The client
// supplies its own id (no ID-generation expression exists in this engine's
// WSL dialect), matching the channel/template nickname convention.

export interface SignalPayload {
  id: string;
  channelId: string;
  type: string;
  text: string;
  sender: string;
  date: string;
  // Numeric-ish value carried alongside the signal (see Message.value's
  // comment in types.ts) - always sent as a plain string ('' when the
  // signal has none), same as type/text/sender, rather than omitted: this
  // WSL dialect's assert/HSet actions don't tolerate a JSON field that's
  // entirely absent (vs. present-but-empty) predictably - see signal/
  // create.wsl's StoreValue state.
  value: string;
  // A TemplateSignal's global number (TemplateSignal.number) - when set,
  // the backend resolves `type` from that preset's own name server-side
  // (ignoring `type` above) and, if `value` above is empty, falls back to
  // the preset's own stored default value too - see signal/create.wsl's
  // CheckSignalNumberGiven branch. Omit/undefined for a plain typed
  // message (not sent as ''  - unlike value, this field genuinely didn't
  // exist before this feature, so create.wsl's $json.signalNumber??""
  // already tolerates it being entirely absent).
  signalNumber?: string;
  // Optional file attachment metadata - the blob is uploaded separately
  // via uploadAttachment (POST /storage); these four fields ride on the
  // signal so a client can render the preview without another round trip.
  // All four are sent together or not at all; each is '' when there's no
  // attachment (same present-but-empty convention as `value`).
  attachmentKey?: string;
  attachmentType?: string;
  attachmentName?: string;
  attachmentSize?: string;
  // The id of another message in this same channel this one replies to -
  // see Message.replyToId's comment in types.ts. Omit/undefined for an
  // ordinary message (create.wsl's $json.replyToId??"" tolerates it being
  // entirely absent, same as signalNumber above).
  replyToId?: string;
}

export interface FetchedSignal {
  id: string;
  channelId: string;
  type: string;
  text: string;
  sender: string;
  senderId?: string;
  date: string;
  // Absent (not '') when the stored signal has no value - fetchSignal
  // normalizes an empty/missing backend field to undefined.
  value?: string;
  // Backend `calc` (a JSON string) parsed - see types.ts SignalCalc. Undefined
  // when absent or unparseable, so a bad blob never breaks a signal.
  calc?: SignalCalc;
  // The TemplateSignal.number this message was triggered from, if any -
  // absent for a plain typed message. Lets the UI resolve this message's
  // preset (color/emoji) directly instead of re-scanning every loaded
  // template by name (see MessagesScreen.tsx's colorForSignal).
  signalNumber?: string;
  // Undefined when the stored signal has no attachment - fetchSignal
  // normalizes the four empty backend fields into one object or nothing.
  attachment?: { key: string; type: string; name: string; size: number };
  // Free-form tags (backend `labels`, stored as a JSON array string) -
  // undefined when empty/absent/unparseable. See normalizeFetchedSignal.
  labels?: string[];
  // Event/observation time (backend `at`) - falls back to `date`
  // server-side, so present on any signal written since the field existed.
  at?: string;
  // Set only on a Multi-Channel Report message (backend `additionalChannelIds`,
  // a JSON array string - see internal/report/generate.go's PostReport) -
  // the extra channels folded into it alongside its own channelId. Absent
  // for every other signal, including a single-channel report.
  additionalChannelIds?: string[];
  // Set only on a Multi-Channel Report - the code-computed confidence
  // level (backend `confidence`, one of High/Medium/Low - see
  // internal/report/generate.go's computeConfidence) and the factors that
  // produced it (backend `confidenceFactors`, a JSON array string). This
  // is the level dictated into the LLM's own prompt, not whatever its
  // "## Confidence" text says - prefer this over evidenceReport.ts's
  // parsed confidenceLevel, which only exists as a fallback for reports
  // generated before this field existed.
  confidence?: string;
  confidenceFactors?: string[];
  // See SignalPayload.replyToId's comment - undefined for an ordinary
  // signal (fetchSignal normalizes the backend's empty string, same as
  // value/signalNumber above).
  replyToId?: string;
  // Set only on a report (backend `number`/`edited` - see internal/
  // report.PostReport and workflows/channel/report_update.wsl): the
  // report's stable per-channel permalink number, and whether an owner
  // has edited its text since the AI posted it. Absent for every other
  // signal.
  number?: string;
  // Report sections the owner switched off (backend `hiddenSections`, a JSON
  // array string). Owners get the full text plus this list; public readers
  // never see it - their text already has those sections cut out.
  hiddenSections?: string[];
  edited?: boolean;
}

// Routes through authorizedBackendRequest (cookie + CSRF), not the plain
// backendRequest above - this endpoint used to have NO auth requirement
// at all, so literally anyone, with no account, could post a message
// into any channel. Now the caller must be the channel's owner or an
// accepted member (see signal/create.wsl's header comment).
export async function sendSignal(signal: SignalPayload): Promise<void> {
  const result = await authorizedBackendRequest('/signal', 'POST', signal);
  if (!result.success) throw new Error(result.error ?? 'Unable to send message.');
}

// A signal's storage key is now "channel:messages:<channelId>:<id>" (see
// signal/read.wsl's header comment), so channelId is a required part of
// the URL here too, not just the id.
//
// Routes through authorizedBackendRequest, same rationale as sendSignal
// above - the caller must be this message's own sender or the channel
// owner (see signal/delete.wsl's header comment).
export async function deleteSignal(channelId: string, id: string): Promise<void> {
  const result = await authorizedBackendRequest(`/signal/${encodeNickname(channelId)}/${encodeNickname(id)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to delete message.');
}

// Deletes every message in a channel and empties its message index - the
// owner "clean chat" action, gated behind a confirmation dialog on the
// channel-edit screen. Owner only, enforced server-side (see
// backend/workflows/signal/clear_messages.wsl). The whole delete happens
// in one pipelined Go call rather than N DELETE /signal round trips.
export async function clearChannelMessages(channelId: string): Promise<{ deletedCount: number }> {
  const result = await authorizedBackendRequest<{ deletedCount: number }>(
    `/channels/${encodeNickname(channelId)}/messages`,
    'DELETE'
  );
  if (!result.success) throw new Error(result.error ?? 'Unable to clear messages.');
  return { deletedCount: result.data?.deletedCount ?? 0 };
}

// Routes through authorizedBackendRequest (cookie + CSRF not needed for a
// GET, but the Authorization/Cookie header itself is), not the plain
// backendRequest above - this endpoint used to have no auth requirement
// at all, so literally anyone could read any channel's messages. Now the
// caller must be able to read the channel at all (owner, member, or
// public - see signal/read.wsl's header comment), which requires a real
// identity to check against.
// Normalises one signal hash (the string->string map a signal is stored
// as) into a FetchedSignal - shared by fetchSignal (single GET) and
// fetchSignals (the bulk list, which now returns the same per-item shape,
// see backend signal/signal.HydrateMessages). `fallbackId` is only used
// by the single fetch, where the requested id is a sensible last resort
// if the stored hash somehow lacks one; the bulk path passes nothing and
// a hash with no id is dropped by the caller.
// The backend stores `labels` as a JSON array string ("[]" when empty -
// see signal/signal.ResolveIngestFields). Parse it back to string[],
// tolerating anything unexpected (old signal with no field, a non-array,
// bad JSON) by returning undefined.
function parseSignalLabels(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw !== 'string' || raw === '' || raw === '[]') return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
    return parsed.map(String).filter(Boolean);
  } catch {
    return undefined;
  }
}

// Backend `calc` is a JSON string (internal/outvalue.Result). Anything that
// doesn't parse to an object with a value and a status is dropped - the legacy
// `value` still shows, nothing throws.
export function parseSignalCalc(raw: unknown): SignalCalc | undefined {
  if (typeof raw !== 'string' || raw === '') return undefined;
  try {
    const c = JSON.parse(raw);
    if (c && typeof c === 'object' && c.value && typeof c.value === 'object' && typeof c.status === 'string') return c as SignalCalc;
  } catch {
    /* fall through */
  }
  return undefined;
}

export function normalizeFetchedSignal(data: any, fallbackId?: string): FetchedSignal | null {
  if (!data || typeof data !== 'object') return null;
  const id = data.id ?? fallbackId;
  if (!id) return null;
  return {
    id,
    channelId: data.channelId ?? '',
    type: data.type ?? '',
    text: data.text ?? '',
    sender: data.sender ?? '',
    senderId: data.senderId || undefined,
    date: data.date ?? '',
    value: data.value || undefined,
    calc: parseSignalCalc(data.calc),
    signalNumber: data.signalNumber || undefined,
    replyToId: data.replyToId || undefined,
    labels: parseSignalLabels(data.labels),
    at: data.at || undefined,
    additionalChannelIds: parseSignalLabels(data.additionalChannelIds),
    confidence: data.confidence || undefined,
    confidenceFactors: parseSignalLabels(data.confidenceFactors),
    number: data.number || undefined,
    hiddenSections: parseSignalLabels(data.hiddenSections),
    edited: data.edited === 'true',
    attachment: data.attachmentKey
      ? {
          key: data.attachmentKey,
          type: data.attachmentType || 'application/octet-stream',
          name: data.attachmentName || 'file',
          size: Number(data.attachmentSize) || 0,
        }
      : undefined,
  };
}

export async function fetchSignal(channelId: string, id: string): Promise<FetchedSignal | null> {
  const result = await authorizedBackendRequest(`/signal/${encodeNickname(channelId)}/${encodeNickname(id)}`, 'GET');
  if (!result.success || !result.data) return null;
  return normalizeFetchedSignal(result.data, id);
}

export interface SignalsPage {
  signals: FetchedSignal[];
  pagination: PaginationMeta;
}

// Fetches a page of a channel's message history in ONE request: the
// backend hydrates the whole newest-first page server-side now (see
// signal/list.wsl / signal/signal.HydrateMessages), so `data` carries the
// full signal objects rather than ids to fetch individually. offset
// defaults to 0 (newest page), limit to DEFAULT_PAGE_SIZE.
// shareToken is optional and only meaningful for a logged-out share-link
// visitor (see PublicChannelViewScreen.tsx) - signal/list.wsl falls back to
// checking it against the channel's enabled share link only when the
// caller has no session at all; a logged-in caller's own owner/member/
// public access is checked first and this param is ignored.
export async function fetchSignals(
  channelId: string,
  offset = 0,
  limit = DEFAULT_PAGE_SIZE,
  shareToken?: string
): Promise<SignalsPage> {
  // See fetchSignal's identical comment - this endpoint requires the
  // caller to be able to read the channel too.
  const tokenQs = shareToken ? `&shareToken=${encodeURIComponent(shareToken)}` : '';
  const page = await authorizedBackendRequest<Paginated<any> & { channelId: string }>(
    `/signals/${encodeNickname(channelId)}?offset=${offset}&limit=${limit}${tokenQs}`,
    'GET'
  );
  if (!page.success || !page.data) {
    return { signals: [], pagination: { offset, limit, total: 0, hasMore: false } };
  }
  const signals = (page.data.data ?? [])
    .map((d) => normalizeFetchedSignal(d))
    .filter((s): s is FetchedSignal => s !== null);
  return { signals, pagination: page.data.meta.pagination };
}

// One report as GET /signals/{channelId}/reports returns it: the signal plus
// where it sits in the channel.
export interface ChannelReport extends FetchedSignal {
  // 0-based place in the channel's newest-first signal list (the offset space
  // of GET /signals/{channelId}?offset=).
  index: number;
  // 1-based place counting from the channel's oldest message.
  position: number;
}

export interface ChannelReportsPage {
  reports: ChannelReport[];
  total: number; // reports in the channel
  totalSignals: number; // every signal in the channel
  hasMore: boolean;
}

// Only the channel's reports (AI-generated and hand-written), newest first,
// each with its position in the channel. Same access rule as fetchSignals.
export async function fetchChannelReports(channelId: string, offset = 0, limit = 20): Promise<ChannelReportsPage> {
  const empty: ChannelReportsPage = { reports: [], total: 0, totalSignals: 0, hasMore: false };
  const page = await authorizedBackendRequest<Paginated<any> & { totalSignals?: number }>(
    `/signals/${encodeNickname(channelId)}/reports?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!page.success || !page.data) return empty;
  const reports: ChannelReport[] = [];
  for (const d of page.data.data ?? []) {
    const sig = normalizeFetchedSignal(d);
    if (!sig) continue;
    reports.push({ ...sig, index: Number(d.index) || 0, position: Number(d.position) || 0 });
  }
  const pagination = page.data.meta?.pagination;
  return {
    reports,
    total: pagination?.total ?? reports.length,
    totalSignals: Number((page.data as any).totalSignals) || 0,
    hasMore: !!pagination?.hasMore,
  };
}

// ---- channel sharing (search + real server-persisted membership) --------
//
// The genuinely real, server-backed membership store: channel:members:
// <nickname> (a Set of userIds) + channel:member:<nickname>:<userId> (one
// RedisJSON doc per member) - see backend/workflows/channel/member_add.wsl's
// header comment. An owner "add" now writes status "invited": the invitee
// must accept (POST .../member/{userId}/accept -> acceptChannelInvite) or
// decline (DELETE .../member/{userId} -> removeChannelMember) before they
// join, and until they do they have no access and the channel is not in
// their list. Pending invites are listed via GET /channels/invites
// (fetchChannelInvites). The other statuses: "pending" = a self-service
// join request awaiting owner approval (requestJoinChannel), "accepted" =
// a real member.

export interface UserSearchHit {
  userId: string;
  value: string; // the matched email or nickname, as stored
}

// GET /search/user/{term} returns raw "<value>:<userId>" hits from two
// separate sorted-set indexes (email, nickname) - see
// backend/workflows/user/search.wsl's header comment for why (no
// full-text search backend, so this is ZRANGEBYLEX-based prefix search).
// Splits on the *last* colon since neither an email nor a "@nickname" can
// contain one, then dedupes by userId (the same account can match both
// indexes for a broad enough term).
function parseSearchHits(raw: string[]): UserSearchHit[] {
  return raw.map((entry) => {
    const idx = entry.lastIndexOf(':');
    return { value: entry.slice(0, idx), userId: entry.slice(idx + 1) };
  });
}

export async function searchUsers(term: string): Promise<UserSearchHit[]> {
  if (term.trim().length === 0) return [];
  appLog(`[Sync] searchUsers(${term}): GET /search/user starting`);
  const result = await authorizedBackendRequest<{ emailMatches: string[]; nicknameMatches: string[] }>(
    `/search/user/${encodeURIComponent(term)}`,
    'GET'
  );
  if (!result.success || !result.data) {
    appLog(`[Sync] searchUsers(${term}): FAILED:`, result.error);
    return [];
  }
  const hits = [...parseSearchHits(result.data.emailMatches ?? []), ...parseSearchHits(result.data.nicknameMatches ?? [])];
  const seen = new Set<string>();
  const deduped = hits.filter((hit) => {
    if (seen.has(hit.userId)) return false;
    seen.add(hit.userId);
    return true;
  });
  appLog(`[Sync] searchUsers(${term}): ${deduped.length} hit(s)`, deduped);
  return deduped;
}

export interface UserProfile {
  id: string;
  nickname: string;
  name: { name: string; surname: string; fullname: string };
  personal: { email: string; phone: string };
}

// GET /profile/id/{userId} - hydrates a search hit (which only carries a
// userId) into a displayable profile, same "list ids then fetch each" two
// step as fetchChannels/fetchTemplates/fetchSignals above.
export async function fetchUserProfileById(userId: string): Promise<UserProfile | null> {
  const result = await authorizedBackendRequest<UserProfile>(`/profile/id/${encodeURIComponent(userId)}`, 'GET');
  return result.success && result.data ? result.data : null;
}

export interface ChannelMember {
  userId: string;
  email: string;
  nickname: string;
  name: string;
  avatarSymbol: string;
  status: string;
  // Set on a member doc written by an owner "invite" (status "invited") -
  // the userId of the owner/co-owner who sent it. "" for a self-service
  // join request or a legacy direct add.
  invitedBy: string;
  requestedAt: string;
  message: string;
  respondedAt: string;
  respondedBy: string;
  // Effective per-member send permissions (roadmap 4a-f). Always present
  // on a member fetched from the server (member_read.wsl returns the whole
  // doc); normalised via normalizeChannelMember so an older backend that
  // predates the matrix still yields a full object.
  permissions: ChannelMemberPermissions;
}

function normalizeChannelMember(data: any): ChannelMember {
  return {
    userId: data.userId ?? '',
    email: data.email ?? '',
    nickname: data.nickname ?? '',
    name: data.name ?? '',
    avatarSymbol: data.avatarSymbol ?? '',
    status: data.status ?? 'accepted',
    invitedBy: data.invitedBy ?? '',
    requestedAt: data.requestedAt ?? '',
    message: data.message ?? '',
    respondedAt: data.respondedAt ?? '',
    respondedBy: data.respondedBy ?? '',
    permissions: {
      ...parseMemberPermissions(data.permissions),
      manageChannel: data.permissions?.manageChannel ?? false,
    },
  };
}

export async function fetchChannelMember(channelNickname: string, userId: string): Promise<ChannelMember | null> {
  // `userId` is '' until the first profile fetch resolves (see types.ts
  // ProfileDraft.userId). The route is /channels/{nickname}/member/{userId}
  // with no empty-{userId} variant, so an unguarded call here 404s on the
  // bare Go router every screen mount - bail before it leaves.
  if (!userId) return null;
  const result = await backendRequest<any>(
    `/channels/${encodeNickname(channelNickname)}/member/${encodeURIComponent(userId)}`,
    'GET'
  );
  return result.success && result.data ? normalizeChannelMember(result.data) : null;
}

// POST /channels/{nickname}/member/{userId}/permissions - edits one
// member's per-member override (roadmap 4a-f). Sends only the keys that
// changed inside a `permissions` object; the backend merges them over the
// member's stored set (see channel/member_permissions.wsl). Owner or
// manageChannel co-owner only - 403 otherwise.
export async function setChannelMemberPermissions(
  channelNickname: string,
  userId: string,
  permissions: Partial<ChannelMemberPermissions>
): Promise<ChannelMember> {
  const result = await authorizedBackendRequest<any>(
    `/channels/${encodeNickname(channelNickname)}/member/${encodeURIComponent(userId)}/permissions`,
    'POST',
    { permissions }
  );
  if (!result.success || !result.data) {
    // {code:"advanced_permissions"} - the resolved permission set didn't
    // match a named role preset and ADVANCED_PERMISSIONS_ENABLED is on
    // (roadmap 4b-f) - see backend/workflows/channel/member_permissions.wsl.
    if (result.code) throw new LimitError(result.error ?? 'Plan limit reached.', result.code);
    throw new Error(result.error ?? 'Unable to update member permissions.');
  }
  return normalizeChannelMember(result.data);
}

// Channel co-ownership (ownerIds). Promote an existing accepted member to
// a full owner, or demote one - both owner-only, and the backend refuses
// to remove the last owner (see backend/workflows/channel/owner_add.wsl /
// owner_remove.wsl). Both return the channel's new ownerIds array.
export async function addChannelOwner(channelNickname: string, userId: string): Promise<string[]> {
  const result = await authorizedBackendRequest<{ ownerIds?: string[] }>(
    `/channels/${encodeNickname(channelNickname)}/owners`,
    'POST',
    { userId }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to add channel owner.');
  return result.data.ownerIds ?? [];
}

export async function removeChannelOwner(channelNickname: string, userId: string): Promise<string[]> {
  const result = await authorizedBackendRequest<{ ownerIds?: string[] }>(
    `/channels/${encodeNickname(channelNickname)}/owner/${encodeURIComponent(userId)}`,
    'DELETE'
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to remove channel owner.');
  return result.data.ownerIds ?? [];
}

// GET /channels/{nickname}/members returns member ids only (no loop
// construct exists server-side to hydrate every doc - see
// backend/workflows/channel/member_list.wsl's comment), same "ids then
// fetch each" pattern as everywhere else in this file.
export async function fetchChannelMembers(channelNickname: string): Promise<ChannelMember[]> {
  const index = await backendRequest<{ userIds: string[] }>(`/channels/${encodeNickname(channelNickname)}/members`, 'GET');
  if (!index.success || !index.data) return [];
  const ids = [...new Set(index.data.userIds ?? [])];
  const members = await Promise.all(ids.map((id) => fetchChannelMember(channelNickname, id)));
  return members.filter((m): m is ChannelMember => m !== null);
}

// Both route through authorizedBackendRequest (cookie + CSRF), not the
// plain backendRequest above - this route used to have no auth at all
// server-side, so anyone could add or remove any channel's members (see
// backend/workflows/channel/member_add.wsl and member_remove.wsl's
// identical comments). Only the channel's real owner, a co-owner
// (manageChannel), or - for removal only - the member themselves ("leave
// channel") can call these now.
export async function addChannelMember(
  channelNickname: string,
  member: { userId: string; email: string; nickname: string; name: string; avatarSymbol: string }
): Promise<ChannelMember> {
  const result = await authorizedBackendRequest<any>(`/channels/${encodeNickname(channelNickname)}/members`, 'POST', member);
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to add member.');
  return normalizeChannelMember(result.data);
}

export async function removeChannelMember(channelNickname: string, userId: string): Promise<void> {
  const result = await authorizedBackendRequest(`/channels/${encodeNickname(channelNickname)}/member/${encodeURIComponent(userId)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to remove member.');
}

// Approves a pending join request (see requestJoinChannel below for how
// one gets created) - turns it into real membership. Not idempotent
// server-side (see backend/workflows/channel/member_approve.wsl's header
// comment): approving an already-accepted or nonexistent request is a
// real error, not a no-op.
export async function approveChannelMember(channelNickname: string, userId: string): Promise<ChannelMember> {
  const result = await authorizedBackendRequest<any>(
    `/channels/${encodeNickname(channelNickname)}/member/${encodeURIComponent(userId)}/approve`,
    'POST',
    { respondedAt: new Date().toISOString() }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to approve this request.');
  return normalizeChannelMember(result.data);
}

// Denying/cancelling a join request reuses removeChannelMember above
// (DELETE /channels/{nickname}/member/{userId}) - its owner/manageChannel/
// self auth already covers both "owner denies" and "requester cancels
// their own request" (see member_remove.wsl's comment). Declining an
// invite reuses it too (the "self" path).

// ---- channel invites (the invitee-facing side of an owner "add") --------

// GET /channels/invites - the nicknames of channels the caller has been
// invited to but hasn't accepted/declined yet (see
// backend/workflows/channel/invite_list.wsl). Nicknames only, same "list
// ids then hydrate" pattern as fetchChannels - the caller resolves each
// channel via fetchChannel and the inviter via fetchChannelMember +
// fetchUserProfileById.
export async function fetchChannelInvites(): Promise<string[]> {
  const result = await authorizedBackendRequest<{ data: string[] }>('/channels/invites', 'GET');
  if (!result.success || !result.data) return [];
  return [...new Set(result.data.data ?? [])];
}

// POST /channels/{nickname}/member/{userId}/accept - the invitee accepts a
// pending invite, becoming a real member (see
// backend/workflows/channel/invite_accept.wsl). Only the invitee can call
// it - userId must be their own. Not idempotent: accepting an already-
// accepted (or nonexistent) invite is an error.
export async function acceptChannelInvite(channelNickname: string, selfUserId: string): Promise<ChannelMember> {
  const result = await authorizedBackendRequest<any>(
    `/channels/${encodeNickname(channelNickname)}/member/${encodeURIComponent(selfUserId)}/accept`,
    'POST',
    { respondedAt: new Date().toISOString() }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to accept this invite.');
  return normalizeChannelMember(result.data);
}

// POST /shared/{nickname}/{token}/request-join - submits a request to
// join a private channel via its share link. Requires shareLink.
// requestToJoin to be on (see setShareLinkRequestToJoin below) and the
// caller to be logged in - see backend/workflows/channel/
// request_access.wsl's header comment for the full rationale (a
// requester needs a real userId to eventually become a member, unlike
// the fully anonymous read-only view at fetchSharedChannel). `requester`
// is the caller's own profile fields (nickname/name/avatarSymbol) - same
// caller-supplies-display-fields pattern addChannelMember above uses,
// since dataApi.ts has no access to the store's own profile state.
export async function requestJoinChannel(
  channelNickname: string,
  token: string,
  requester: { nickname: string; name: string; avatarSymbol: string },
  options: { password?: string; message?: string }
): Promise<{ status: string }> {
  const result = await authorizedBackendRequest<{ status: string }>(
    `/shared/${encodeNickname(channelNickname)}/${encodeURIComponent(token)}/request-join`,
    'POST',
    {
      password: options.password ?? '',
      message: options.message ?? '',
      requestedAt: new Date().toISOString(),
      nickname: requester.nickname,
      name: requester.name,
      avatarSymbol: requester.avatarSymbol,
    }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to submit your join request.');
  return result.data;
}

// ---- share links (public, read-only channel access) ---------------------
//
// A separate concept from real membership above: a share link grants
// anyone who has the link (and password, if one is set) a read-only view
// of this channel - no account, no invite, no login. See
// backend/workflows/channel/share_link_enable.wsl's header comment for the
// storage shape and backend/workflows/channel/shared_view.wsl for the
// public verification endpoint this resolves to.

export interface ShareLinkStatus {
  enabled: boolean;
  token: string;
  passwordProtected: boolean;
  requestToJoin: boolean;
}

// Routes through authorizedBackendRequest (cookie + CSRF, or Bearer) -
// this endpoint used to have NO auth requirement at all, letting anyone
// who knew a channel's nickname fetch its live share-link token directly,
// defeating the point of a "secret" link (see backend/workflows/channel/
// share_link_status.wsl's header comment). Owner/manageChannel only now.
export async function fetchShareLinkStatus(channelNickname: string): Promise<ShareLinkStatus | null> {
  const result = await authorizedBackendRequest<ShareLinkStatus>(`/channels/${encodeNickname(channelNickname)}/share-link`, 'GET');
  return result.success && result.data ? result.data : null;
}

// Enabling always issues a *fresh* token server-side, even on re-enable
// after a disable (see share_link_enable.wsl's comment) - any previously
// shared link is permanently dead once this is called again. Only ever
// flips `enabled` - passwordProtected/requestToJoin are independent
// switches now, managed by setShareLinkPassword/setShareLinkRequestToJoin
// below, and "searchable as public" is independent too (see
// setChannelSearchable) - see share_link_enable.wsl's header comment for
// the earlier, now-reversed design where these were all coupled together.
export async function enableShareLink(channelNickname: string): Promise<ShareLinkStatus> {
  const result = await authorizedBackendRequest<ShareLinkStatus>(`/channels/${encodeNickname(channelNickname)}/share-link/enable`, 'POST', {});
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to enable share link.');
  return result.data;
}

// Returns `true` rather than void: TanStack Query v5's queryClient.query()
// (fetchQuery under the hood) treats a queryFn that resolves to `undefined`
// as an error ("Query data cannot be undefined"), even though the server
// call succeeded - that left toggleShareLink's disable path throwing into
// its catch block (stale error toast, switch stuck on) despite the backend
// having already disabled the link. See ChannelShareScreen.tsx's
// toggleShareLink for the caller.
export async function disableShareLink(channelNickname: string): Promise<true> {
  const result = await authorizedBackendRequest(`/channels/${encodeNickname(channelNickname)}/share-link/disable`, 'POST', {});
  if (!result.success) throw new Error(result.error ?? 'Unable to disable share link.');
  return true;
}

// Sets or clears the share link's password without rotating its token
// (unlike enableShareLink above) - see backend/workflows/channel/
// share_link_password.wsl's header comment for why this is its own
// endpoint: it's what makes "require a password" a genuinely independent
// switch instead of forcing a fresh link every time it changes.
export async function setShareLinkPassword(channelNickname: string, password: string): Promise<{ passwordProtected: boolean }> {
  const result = await authorizedBackendRequest<{ passwordProtected: boolean }>(
    `/channels/${encodeNickname(channelNickname)}/share-link/password`,
    'POST',
    { password }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to update the share link password.');
  return result.data;
}

// Enables/disables submitting join requests via the share link (see
// requestJoinChannel above) - never touches the token.
export async function setShareLinkRequestToJoin(channelNickname: string, enabled: boolean): Promise<{ requestToJoin: boolean }> {
  const result = await authorizedBackendRequest<{ requestToJoin: boolean }>(
    `/channels/${encodeNickname(channelNickname)}/share-link/request-to-join`,
    'POST',
    { enabled }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to update request-to-join.');
  return result.data;
}

// Makes a channel discoverable in public search (channel/search.wsl),
// independent of its share link - see backend/workflows/channel/
// searchable_enable.wsl's header comment.
export async function setChannelSearchable(channelNickname: string, enabled: boolean): Promise<{ isPublic: boolean }> {
  const path = enabled ? 'searchable/enable' : 'searchable/disable';
  const result = await authorizedBackendRequest<{ isPublic: boolean }>(`/channels/${encodeNickname(channelNickname)}/${path}`, 'POST', {});
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to update searchability.');
  return result.data;
}

// POST /channels/{nickname}/invite-email - has the backend itself send the
// invite (see backend/workflows/channel/invite_email.wsl's header comment
// on why this is real SMTP now, not a mailto: handoff, and why it's
// channel-scoped/purpose-built rather than a generic send-any-email
// endpoint). Requires the share link to already be enabled - the caller
// is expected to call enableShareLink first if needed, same as the
// SMS/WhatsApp share path.
export async function inviteChannelByEmail(channelNickname: string, email: string): Promise<void> {
  const result = await backendRequest(`/channels/${encodeNickname(channelNickname)}/invite-email`, 'POST', { email });
  if (!result.success) throw new Error(result.error ?? 'Unable to send the invite email.');
}

export interface SharedChannelView {
  nickname: string;
  title: string;
  description: string;
  avatarSymbol: string;
  avatarImageKey: string;
  accentHex: string;
  viewType: string;
  viewChartType: string;
  viewDesign: string;
  chartSettings: Partial<ChartSettings>;
  publicChartFiltersAllowed: boolean;
  requestToJoin: boolean;
  // #11 pricing enforcement - true when the "Powered by zmist" badge may
  // be hidden (any owner is on a badgeRemovable tier - see
  // channel/sharelink.ResolveBadgeRemovable).
  badgeRemovable: boolean;
}

// POST /shared/{nickname}/{token} - the public, unauthenticated
// verification endpoint. On success, the caller fetches this channel's
// messages via the already-public fetchSignals(nickname) above (GET
// /signals/{channelId} has no auth check of its own - see
// shared_view.wsl's comment on why this endpoint's real job is gating the
// legitimate share-link flow, not retrofitting auth onto that route).
export async function fetchSharedChannel(nickname: string, token: string, password: string): Promise<SharedChannelView> {
  const result = await backendRequest<SharedChannelView>(
    `/shared/${encodeNickname(nickname)}/${encodeNickname(token)}`,
    'POST',
    { password }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load this shared channel.');
  return result.data;
}

export interface PublicChannelView {
  nickname: string;
  title: string;
  description: string;
  tags: string[];
  avatarSymbol: string;
  avatarImageKey: string;
  accentHex: string;
  viewType: string;
  viewChartType: string;
  viewDesign: string;
  chartSettings: Partial<ChartSettings>;
  publicChartFiltersAllowed: boolean;
  badgeRemovable: boolean;
  // Message bunching window in seconds for this channel's chat (0 = off,
  // same Channel.bunchingInterval field, owner-set in the channel edit
  // form) - see backend/workflows/channel/public_view.wsl.
  bunchingInterval: number;
}

// GET /public/channels/{nickname} - the token-less counterpart to
// fetchSharedChannel above: gated purely on isPublic && !blocked
// (channel/public_view.wsl), no share-link token or password involved.
// This is the live-showcase homepage's "Open channel" target - every
// public channel has this URL, whether or not its owner ever turned on a
// share link. Messages/charts are fetched the same way as the share-link
// flow (fetchSignals(nickname) / the chart endpoint), both now also
// anonymous-public-gated - see signal/list.wsl's AnonymousCheckIsPublic
// and stats/channel_chart.wsl's identical state.
export async function fetchPublicChannel(nickname: string): Promise<PublicChannelView> {
  const result = await backendRequest<PublicChannelView>(`/public/channels/${encodeNickname(nickname)}`, 'GET');
  if (!result.success || !result.data) throw new Error(result.error ?? 'This channel is not public.');
  return result.data;
}

export interface PublicReport {
  id: string;
  text: string;
  sender: string;
  date: string;
  confidence?: string;
  confidenceFactors?: string[];
  additionalChannelIds?: string[];
  // number/rangeStart/rangeEnd are stamped by internal/report.PostReport
  // on every report it posts (see backend's ZMIST_PUBLIC_CHANNELS_
  // PRODUCT_GTM_PLAN.md section 5-7): a stable per-channel sequential
  // number (the public permalink's {number}) plus the date range the
  // report's analysis actually covered - "data captured" as distinct from
  // the channel's current live data. number is 0 on a report posted
  // before this field existed.
  number: number;
  rangeStart?: string;
  rangeEnd?: string;
  // Set by workflows/channel/report_update.wsl the first time an owner
  // edits this report's text - lets the UI distinguish "as the AI
  // generated it" from "edited since."
  edited?: boolean;
}

function parsePublicReport(r: Record<string, string>): PublicReport {
  const parseJsonArray = (raw: string | undefined): string[] | undefined => {
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : undefined;
    } catch {
      return undefined;
    }
  };
  return {
    id: r.id ?? '',
    text: r.text ?? '',
    sender: r.sender ?? '',
    date: r.date ?? '',
    confidence: r.confidence,
    confidenceFactors: parseJsonArray(r.confidenceFactors),
    additionalChannelIds: parseJsonArray(r.additionalChannelIds),
    number: r.number ? parseInt(r.number, 10) : 0,
    rangeStart: r.rangeStart,
    rangeEnd: r.rangeEnd,
    edited: r.edited === 'true',
  };
}

// GET /public/channels/{nickname}/report - the channel's latest AI report
// (channel/public_report.wsl), structured instead of pulled out of the
// message stream by convention. found:false (not an error) when the
// channel has never had a report generated.
export async function fetchPublicReport(nickname: string): Promise<PublicReport | null> {
  const result = await backendRequest<{ found: boolean; report?: Record<string, string> }>(
    `/public/channels/${encodeNickname(nickname)}/report`,
    'GET'
  );
  if (!result.success || !result.data?.found || !result.data.report) return null;
  return parsePublicReport(result.data.report);
}

// GET /public/channels/{nickname}/reports/{number} - one of the channel's
// reports by its stable sequential number (channel/
// public_report_by_number.wsl) - the permalink counterpart to
// fetchPublicReport above (always the newest). found:false (not an error)
// when that number was never issued for this channel.
export async function fetchPublicReportByNumber(nickname: string, number: number): Promise<PublicReport | null> {
  const result = await backendRequest<{ found: boolean; report?: Record<string, string> }>(
    `/public/channels/${encodeNickname(nickname)}/reports/${number}`,
    'GET'
  );
  if (!result.success || !result.data?.found || !result.data.report) return null;
  return parsePublicReport(result.data.report);
}

// GET /public/channels/{nickname}/reports?offset=&limit= - a newest-first
// page of every report ever posted for this channel (channel/
// public_report_history.wsl), for a "Report #N - <date>" history list
// linking to fetchPublicReportByNumber's permalinks.
export async function fetchPublicReportHistory(
  nickname: string,
  offset = 0,
  limit = DEFAULT_PAGE_SIZE
): Promise<{ reports: PublicReport[]; pagination: PaginationMeta }> {
  const result = await backendRequest<Paginated<Record<string, string>>>(
    `/public/channels/${encodeNickname(nickname)}/reports?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) {
    return { reports: [], pagination: { offset, limit, total: 0, hasMore: false } };
  }
  return { reports: (result.data.data ?? []).map(parsePublicReport), pagination: result.data.meta.pagination };
}

// PATCH /channels/{nickname}/reports/{number} - owner (or manageChannel
// co-owner) only, see backend/workflows/channel/report_update.wsl. Edits
// an already-posted report's text in place; never touches its frozen
// checkpoint data (fetchReportCheckpoint below reads a separate,
// write-once blob). Throws on failure (not-owner, report not found) so
// the editor UI can surface the error directly.
// Addressed by the report message's own id, not its public permalink
// number (backend/workflows/channel/report_update.wsl) - a report only
// has a number if internal/report.PostReport posted it (an AI-generated
// report); one typed directly into the channel, or predating the
// numbering feature, has no number and still needs to be editable.
export async function updateReport(nickname: string, id: string, text: string): Promise<void> {
  const result = await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/reports/${encodeNickname(id)}`, 'PUT', { text });
  if (!result.success) throw new Error(result.error ?? 'Unable to save this report.');
}

// DELETE /channels/{nickname}/reports/{id} - permanently removes one posted
// report (owner or manageChannel). Throws on failure.
export async function deleteReport(nickname: string, id: string): Promise<void> {
  const result = await authorizedBackendRequest(`/channels/${encodeNickname(nickname)}/reports/${encodeNickname(id)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Unable to delete this report.');
}

export interface ReportCheckpointPage {
  // false means this report number has no checkpoint at all (predates
  // the feature, or capture failed at generation time - see internal/
  // report.PostReport's comment on why that's tolerated) - distinct from
  // a checkpoint that simply has no messages in it (found: true, signals: []).
  found: boolean;
  signals: FetchedSignal[];
  pagination: PaginationMeta;
}

// GET /public/channels/{nickname}/reports/{number}/checkpoint - a page of
// a report's frozen historical Messages (channel/
// public_report_checkpoint.wsl) exactly as they stood when the report
// posted. This is the data behind a checkpoint's Messages/Charts tabs -
// permanent record, never live.
export async function fetchReportCheckpoint(
  nickname: string,
  number: number,
  offset = 0,
  limit = DEFAULT_PAGE_SIZE
): Promise<ReportCheckpointPage> {
  const result = await backendRequest<{ found: boolean; data: unknown[]; meta: { pagination: PaginationMeta } }>(
    `/public/channels/${encodeNickname(nickname)}/reports/${number}/checkpoint?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) {
    return { found: false, signals: [], pagination: { offset, limit, total: 0, hasMore: false } };
  }
  return {
    found: result.data.found,
    signals: (result.data.data ?? []).map((d) => normalizeFetchedSignal(d)).filter((s): s is FetchedSignal => s !== null),
    pagination: result.data.meta.pagination,
  };
}

export interface PublicCatalogCard {
  nickname: string;
  title: string;
  description: string;
  tags: string[];
  avatarSymbol: string;
  avatarImageKey?: string;
  accentHex: string;
  ownerCount: number;
  lastUpdatedAt: number;
  latestSignal: string;
  latestValue: number;
  // [unixSeconds, value] pairs of the busiest signal type, oldest first.
  spark?: [number, number][];
}

// GET /public/channels/catalog?q=&offset=&limit= - the live-showcase
// homepage's catalog feed (channel/public_catalog.wsl). q empty browses
// channel:rank:public (featured/best-first, same index /channels/search
// uses for its own empty-query "browse the best" path); a non-empty q
// prefix-searches the same public indexes /channels/search does.
// GET /public/channels/{nickname}/changes?days= - window-over-window summary
// per signal type (channel/public_changes.wsl). Returned as-is: the shape is
// owned by the backend (see docs/architecture/AI_TOOL_REGISTRY.md).
export async function fetchPublicChanges(nickname: string, days = 7): Promise<unknown | null> {
  const result = await backendRequest<{ data: unknown }>(
    `/public/channels/${encodeURIComponent(nickname)}/changes?days=${days}`,
    'GET'
  );
  if (!result.success || !result.data) return null;
  return result.data.data ?? null;
}

// GET /public/channels/{nickname}/signals?offset=&limit= (limit <= 50) - the
// tokenless signal feed behind the public page's chart and Raw Data
// (channel/public_signals.wsl). A plain unauthenticated request: no session,
// no CSRF, no share token. The backend sends an explicit whitelist (no
// sender, attachments or reply refs), so `sender` is a neutral label here
// rather than a person's handle.
export const PUBLIC_SIGNALS_PAGE_SIZE = 50;

export async function fetchPublicSignals(
  nickname: string,
  offset = 0,
  limit = PUBLIC_SIGNALS_PAGE_SIZE
): Promise<{ signals: FetchedSignal[]; hasMore: boolean; limit: number }> {
  const result = await backendRequest<{ signals?: any[]; hasMore?: boolean; limit?: number }>(
    `/public/channels/${encodeNickname(nickname)}/signals?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) return { signals: [], hasMore: false, limit };
  const signals = (result.data.signals ?? [])
    .map((d) => normalizeFetchedSignal({ ...d, channelId: nickname, sender: 'Signal' }))
    .filter((x): x is FetchedSignal => x !== null);
  return { signals, hasMore: !!result.data.hasMore, limit: result.data.limit ?? limit };
}

export async function fetchPublicCatalog(q = '', offset = 0, limit = 20): Promise<{ cards: PublicCatalogCard[]; total: number }> {
  const result = await backendRequest<{ data: PublicCatalogCard[]; meta: { pagination: { total: number } } }>(
    `/public/channels/catalog?q=${encodeURIComponent(q)}&offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) return { cards: [], total: 0 };
  return { cards: result.data.data ?? [], total: result.data.meta?.pagination?.total ?? 0 };
}

// ---- file storage (avatars + message attachments) ------------------
//
// POST /storage / GET /storage/{key} (backend/workflows/storage/*.wsl,
// currently local-disk-backed - see modules/storage/transitions/local.go)
// is JSON-only (no multipart/form-data support), so a file is base64-
// encoded client-side before upload and base64-decoded again on download -
// see AttachmentPicker/AvatarImagePicker (the file-input/FileReader step)
// and AttachmentView/AvatarView (the fetch-and-cache-as-a-data-URL step
// for display). Keys deliberately never contain "/" - the backend route
// is "/storage/{key}", a single path segment (Go 1.22+ ServeMux syntax),
// so a key with a slash in it simply wouldn't route at all.
//
// Both routes now require an authenticated caller (they had NO check at
// all before - anyone could fill the disk or read any blob back), so
// these go through authorizedBackendRequest, not the plain one - the
// httpOnly session cookie rides along either way, but the upload also
// needs the X-CSRF-Token header authorizedBackendRequest attaches for a
// non-GET. The backend also enforces a 10 MB size cap and a MIME
// allowlist (images / PDF / CSV / text - see modules/storage/transitions/
// limits.go); a rejection surfaces here as a thrown Error with the
// backend's own message.

export interface UploadedAttachment {
  key: string;
  type: string;
  name: string;
  size: number;
}

// Reads a File as base64 (the part after the "data:...;base64," prefix),
// POSTs it to /storage under a fresh key, and returns the metadata a
// message needs to reference and render it. Shared by the avatar picker
// and the compose-bar attachment button.
export async function uploadAttachment(file: File): Promise<UploadedAttachment> {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  });
  const ext = (file.name.split('.').pop() || file.type.split('/')[1] || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  const contentType = file.type || 'application/octet-stream';
  const key = `attachment-${uid()}.${ext}`;
  const result = await authorizedBackendRequest<{ key: string; size?: number }>('/storage', 'POST', { key, contentType, data: base64 });
  if (!result.success) throw new Error(result.error ?? 'Unable to upload this file.');
  return { key, type: contentType, name: file.name, size: result.data?.size ?? Math.floor((base64.length * 3) / 4) };
}

export async function uploadStorageBase64(key: string, contentType: string, base64Data: string): Promise<void> {
  const result = await authorizedBackendRequest<{ key: string }>('/storage', 'POST', { key, contentType, data: base64Data });
  if (!result.success) throw new Error(result.error ?? 'Unable to upload file.');
}

// Which rendition GET /storage/{key}?size= returns. thumb/medium/large are
// 128/512/1280 px on the long edge (backend/config/uploads.json), generated
// after the upload passes its ClamAV scan; anything the server doesn't have
// (source already smaller, non-image, thumbnails off/failed, a file uploaded
// before thumbnails existed) silently falls back to the original.
export type StorageSize = 'thumb' | 'medium' | 'large' | 'original';

// An upload is answered 423 until its virus scan (and, for size=thumb, its
// thumbnails) finish - normally a second or two, longer if clamd is busy.
// Polled with a gentle backoff; if it is still not ready after ~45 s this
// throws StoragePendingError, so the caller's query stays un-cached and the
// next mount tries again instead of remembering a permanent "no image".
export class StoragePendingError extends Error {
  constructor() {
    super('The file is still being processed.');
    this.name = 'StoragePendingError';
  }
}

const STORAGE_POLL_DELAYS_MS = [700, 1000, 1500, 2000, 3000, 4000, 5000, 5000, 5000, 5000, 5000, 5000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchStorageDataUrl(key: string, size: StorageSize = 'original'): Promise<string | null> {
  const query = size === 'original' ? '' : `?size=${size}`;
  for (let attempt = 0; ; attempt++) {
    const result = await authorizedBackendRequest<{ key: string; contentType: string; data: string }>(
      `/storage/${encodeURIComponent(key)}${query}`,
      'GET'
    );
    if (result.success && result.data) {
      return `data:${result.data.contentType || 'application/octet-stream'};base64,${result.data.data}`;
    }
    if (result.status !== 423) return null; // 404: gone, infected or never existed
    if (attempt >= STORAGE_POLL_DELAYS_MS.length) throw new StoragePendingError();
    await sleep(STORAGE_POLL_DELAYS_MS[attempt]);
  }
}

// Back-compat aliases - the avatar call sites (ProfileScreen, channel/
// template manage screens, AvatarView) predate the rename above.
export const uploadAvatarImage = uploadStorageBase64;
export const fetchAvatarImageDataUrl = fetchStorageDataUrl;

// ---- active login sessions ---------------------------------------------
//
// Backed by GET /sessions, DELETE /sessions/{id}, POST
// /sessions/revoke-others (backend/workflows/user/sessions_*.wsl). A
// "session" is the server-side bff:session record - the browser holds its
// id in an httpOnly cookie, a native client sends it as its Bearer token -
// so both this device's session and every other one show up here and can
// be ended individually. `current` marks this device.

export interface ActiveSession {
  id: string;
  platform: string; // "web" | "ios" | "android" | "api"
  ip: string;
  userAgent: string;
  issuedAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

export async function fetchSessions(): Promise<ActiveSession[]> {
  const result = await authorizedBackendRequest<{ sessions: ActiveSession[] }>('/sessions', 'GET');
  return result.success && result.data ? result.data.sessions ?? [] : [];
}

export async function revokeSession(id: string): Promise<boolean> {
  const result = await authorizedBackendRequest<{ revoked: boolean }>(`/sessions/${encodeURIComponent(id)}`, 'DELETE');
  if (!result.success) throw new Error(result.error ?? 'Could not revoke that session.');
  return !!result.data?.revoked;
}

export async function revokeOtherSessions(): Promise<number> {
  const result = await authorizedBackendRequest<{ revoked: number }>('/sessions/revoke-others', 'POST');
  if (!result.success) throw new Error(result.error ?? 'Could not sign out other sessions.');
  return result.data?.revoked ?? 0;
}

// ---- integration Activity & health (roadmap #9 Phase 2) -----------------------
//
// Backed by backend/internal/integrationrun: a run record per delivery, a
// stable error taxonomy, and a health state derived from them.
// - GET  /mappings/{nickname}/activity?offset=&limit=  owner only
// - GET/POST /integrations/alerts                      where health Signals go

export type IntegrationHealthState = 'healthy' | 'waiting' | 'no_recent' | 'delayed' | 'attention';

export interface IntegrationRunError {
  code: string;
  stage: string;
  retryable: boolean;
  message: string;
  action: string;
}

export interface IntegrationRun {
  id: string;
  source: 'webhook' | 'prometheus_remote_write' | 'agent' | string;
  startedAt: number;
  finishedAt: number;
  status: 'success' | 'empty' | 'failed';
  stage: string;
  inputCount: number;
  signalCount: number;
  rejectedCount: number;
  mappingVersion?: number;
  errorCode?: string;
  error?: IntegrationRunError;
}

export interface IntegrationErrorGroup extends IntegrationRunError {
  count: number;
  firstAt: number;
  lastAt: number;
  recurring: boolean;
}

export interface IntegrationActivity {
  health: { state: IntegrationHealthState; label: string; reason: string };
  stats: {
    totalRuns: number;
    totalSignals: number;
    lastRunAt: number;
    lastSuccessAt: number;
    lastFailureAt: number;
    consecutiveFailures: number;
    cadenceSec: number;
  };
  errors: IntegrationErrorGroup[];
  runs: IntegrationRun[];
  total: number;
}

export async function fetchIntegrationActivity(nickname: string, offset = 0, limit = 20): Promise<IntegrationActivity> {
  const result = await authorizedBackendRequest<IntegrationActivity>(
    `/mappings/${encodeNickname(nickname)}/activity?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load activity.');
  return result.data;
}

// This mapping's slice of webhook:ingest's pending (claimed, not yet
// ACK'd) entries - backend's webhookrecv.ListPendingForMapping. Normally
// empty; only meaningful for a sourceType:"webhook" mapping (every other
// connector never touches this queue).
export interface WebhookQueueItem {
  id: string;
  consumer: string;
  idleMs: number;
  deliveries: number;
}
export interface WebhookQueuePage {
  items: WebhookQueueItem[];
  total: number;
  offset: number;
  limit: number;
}

export async function fetchWebhookQueueStatus(nickname: string, offset = 0, limit = 10): Promise<WebhookQueuePage> {
  const result = await authorizedBackendRequest<WebhookQueuePage>(
    `/mappings/${encodeNickname(nickname)}/webhook-queue?offset=${offset}&limit=${limit}`,
    'GET'
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load webhook queue status.');
  return result.data;
}

export async function fetchIntegrationAlertChannel(): Promise<string> {
  const result = await authorizedBackendRequest<{ channelId: string }>('/integrations/alerts', 'GET');
  return result.success && result.data ? result.data.channelId ?? '' : '';
}

// "" turns health alerts off.
export async function setIntegrationAlertChannel(channelId: string): Promise<string> {
  const result = await authorizedBackendRequest<{ channelId: string }>('/integrations/alerts', 'POST', { channelId });
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to save the alert channel.');
  return result.data.channelId ?? '';
}

// ---- opt-in raw-payload debug capture (§13: "optional debug samples have a
// short TTL and field-level redaction") ------------------------------------
//
// - GET/POST /mappings/{nickname}/debug-capture   arm (1h, self-expiring) or disarm
// - GET      /mappings/{nickname}/runs/{id}/sample  one run's captured raw+mapped pair

export interface DebugCaptureStatus {
  enabled: boolean;
  expiresAt: number;
}

export interface DebugSample {
  sample: unknown;
  mapped: Array<{ name: string; value: string; labels: string[] }>;
  capturedAt: number;
}

export async function fetchDebugCaptureStatus(nickname: string): Promise<DebugCaptureStatus> {
  const result = await authorizedBackendRequest<DebugCaptureStatus>(
    `/mappings/${encodeNickname(nickname)}/debug-capture`,
    'GET'
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to read debug capture status.');
  return result.data;
}

export async function setDebugCapture(nickname: string, enabled: boolean): Promise<DebugCaptureStatus> {
  const result = await authorizedBackendRequest<DebugCaptureStatus>(
    `/mappings/${encodeNickname(nickname)}/debug-capture`,
    'POST',
    { enabled }
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to set debug capture.');
  return result.data;
}

// null: no sample was captured for that run (capture wasn't armed, or it expired) - not an error.
export async function fetchDebugSample(nickname: string, runId: string): Promise<DebugSample | null> {
  const result = await authorizedBackendRequest<DebugSample>(
    `/mappings/${encodeNickname(nickname)}/runs/${encodeURIComponent(runId)}/sample`,
    'GET'
  );
  if (result.status === 404) return null;
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load the debug sample.');
  return result.data;
}

// ---- Salesforce connector (Phase 4 of docs/SIGNAL_TEMPLATES_AS_MAPPING_
// SCHEMA_PLAN.md) - OAuth connect/exchange for a mapping, mirroring
// authApi.ts's startOAuthSignIn/completeOAuthSignIn shape but for a
// per-mapping API credential rather than a login. See
// modules/mapping/transitions/activity.go's Connect/CompleteSalesforceConnect.
//
// - GET  /mappings/{nickname}/salesforce/connect-url   owner-gated: a ready-made authorize URL
// - POST /mappings/salesforce/exchange                 no session - the one-time state token names the mapping

export interface SalesforceConnectUrl {
  url: string;
  state: string;
}

export async function fetchSalesforceConnectUrl(nickname: string): Promise<SalesforceConnectUrl> {
  const result = await authorizedBackendRequest<SalesforceConnectUrl>(
    `/mappings/${encodeNickname(nickname)}/salesforce/connect-url`,
    'GET'
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to start the Salesforce connection.');
  return result.data;
}

export async function completeSalesforceConnect(code: string, state: string): Promise<{ nickname: string }> {
  const result = await backendRequest<{ nickname: string }>('/mappings/salesforce/exchange', 'POST', { code, state });
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to complete the Salesforce connection.');
  return result.data;
}

// ---- mapping versions: draft / publish / rollback (roadmap #9 Phase 3) --------
//
// Backed by backend/internal/mappingversion. The mapping's stored spec IS the
// active version; a draft is a separate, unpublished edit of it. Versions
// carry RULES only - agent credentials are never returned, saved in a draft,
// or restored by a rollback.
// - GET    /mappings/{n}/versions            active + draft + history
// - POST   /mappings/{n}/draft               {spec, baseVersion}        409 if the active version moved
// - DELETE /mappings/{n}/draft
// - POST   /mappings/{n}/publish             {expectedVersion, note?}   409 stale, 400 no draft
// - POST   /mappings/{n}/rollback            {toVersion, expectedVersion}
// Publishing / rolling back changes what FUTURE deliveries do; nothing is replayed.

export interface MappingVersionSummary {
  version: number;
  publishedAt: number;
  publishedBy: string;
  note?: string;
  source: 'initial' | 'publish' | 'rollback' | 'edit';
  rolledBackTo?: number;
}

export interface MappingDraft {
  spec: Record<string, unknown>;
  baseVersion: number;
  updatedAt: number;
  updatedBy: string;
}

export interface MappingVersionsState {
  activeVersion: number;
  activeSpec: Record<string, unknown>;
  draft: MappingDraft | null;
  versions: MappingVersionSummary[];
}

async function versionRequest<T>(nickname: string, path: string, method: string, body?: unknown): Promise<T> {
  const result = await authorizedBackendRequest<T>(`/mappings/${encodeNickname(nickname)}${path}`, method, body);
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Request failed.', result.status);
  return result.data;
}

export const fetchMappingVersions = (nickname: string) => versionRequest<MappingVersionsState>(nickname, '/versions', 'GET');

export async function saveMappingDraft(nickname: string, spec: Record<string, unknown>, baseVersion: number): Promise<MappingDraft> {
  return (await versionRequest<{ draft: MappingDraft }>(nickname, '/draft', 'POST', { spec, baseVersion })).draft;
}

export async function discardMappingDraft(nickname: string): Promise<void> {
  await versionRequest<{ discarded: boolean }>(nickname, '/draft', 'DELETE');
}

export const publishMappingDraft = (nickname: string, expectedVersion: number, note?: string) =>
  versionRequest<{ version: number; publishedAt: number }>(nickname, '/publish', 'POST', { expectedVersion, note: note || undefined });

export const rollbackMappingVersion = (nickname: string, toVersion: number, expectedVersion: number) =>
  versionRequest<{ version: number; rolledBackTo: number; publishedAt: number }>(nickname, '/rollback', 'POST', { toVersion, expectedVersion });

export interface UnmappedValue {
  value: string;
  count: number;
}

export interface UnmappedValuesResult {
  auditable: boolean;
  unmapped: UnmappedValue[];
  checked: number;
}

// GET /mappings/{nickname}/unmapped - see backend/modules/mapping/
// transitions/activity.go's GetUnmappedValues for what "auditable" and the
// counts actually mean (a post-hoc audit of already-stored Signals against
// this mapping's own signalRef/name value map, not a live trace).
export const fetchUnmappedValues = (nickname: string) => versionRequest<UnmappedValuesResult>(nickname, '/unmapped', 'GET');

export interface AutoMapResult {
  auditable: boolean;
  applied: number;
  version?: number;
}

// POST /mappings/{nickname}/auto-map - server-authoritative version of the
// exact-name-match policy LiveMappingCard.tsx's own autoMapAll used to
// duplicate client-side (see backend/modules/mapping/transitions/
// automap.go's AutoMap for the exact rule): resolves every value the live
// unmapped-values audit finds and republishes in one shot. No body - it
// operates on whatever's already saved (draft if one exists, else active).
export const postAutoMap = (nickname: string) => versionRequest<AutoMapResult>(nickname, '/auto-map', 'POST');

// ---- mapping discovery: live "Discover" for a "system" (host-metrics)
// mapping (POST /mappings/{n}/discover, GET /mappings/{n}/discovery) ----
//
// POST pushes a request over Cue to a connected zagentc daemon that owns
// this mapping - the agent answers asynchronously (seconds, not
// milliseconds: its own dry pass takes about a second, plus the Cue round
// trip), so this returns only a requestId. GET then polls for the stored
// answer - {found:false} is the normal state while nothing's answered
// yet, not an error. See backend/modules/mapping/transitions/discovery.go's
// header comment for the full round trip and zagentc/src/sysmetrics.h for
// exactly what `describe` contains.

export interface DiscoverRequest {
  requestId: string;
}

export interface SystemDescribe {
  host: string;
  collectors: Record<string, number>; // collector id -> distinct metric-name count
  cpuCores: number;
  mounts: string[];
  interfaces: string[];
  virtualInterfaces: string[]; // subset of `interfaces` hidden by default (veth/docker/br-/...)
  devices: string[];
  sensors: string[];
}

export interface MappingDiscovery {
  found: boolean;
  requestId?: string;
  describe?: SystemDescribe;
  reportedAt?: number;
}

export const requestMappingDiscovery = (nickname: string) => versionRequest<DiscoverRequest>(nickname, '/discover', 'POST');

export const fetchMappingDiscovery = (nickname: string) => versionRequest<MappingDiscovery>(nickname, '/discovery', 'GET');

// ---- mapping credentials: CredentialRef (roadmap #9 Phase 4) ------------------
//
// An integration's scrape credentials (bearerToken / basicAuth / headers) are
// stored apart from its spec (backend/internal/credstore). The web is
// WRITE-ONLY: it can set or remove them and learn WHICH are configured, but
// never read a value back - only the agent (owner session, on the customer's
// own infrastructure) does.
// - GET  /mappings/{n}/credentials?status=1   {configured: string[]}
// - POST /mappings/{n}/credentials            any of bearerToken, basicAuth, headers; null/""/{} removes

export type CredentialField = 'bearerToken' | 'basicAuth' | 'headers';

export interface MappingCredentialsInput {
  bearerToken?: string | null;
  basicAuth?: { username: string; password: string } | null;
  headers?: Record<string, string> | null;
}

export async function fetchMappingCredentialStatus(nickname: string): Promise<CredentialField[]> {
  const result = await authorizedBackendRequest<{ configured: CredentialField[] }>(
    `/mappings/${encodeNickname(nickname)}/credentials?status=1`,
    'GET'
  );
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Unable to load credentials.', result.status);
  return result.data.configured ?? [];
}

export async function saveMappingCredentials(nickname: string, input: MappingCredentialsInput): Promise<CredentialField[]> {
  const result = await authorizedBackendRequest<{ configured: CredentialField[] }>(
    `/mappings/${encodeNickname(nickname)}/credentials`,
    'POST',
    input
  );
  if (!result.success || !result.data) throw new RequestError(result.error ?? 'Unable to save credentials.', result.status);
  return result.data.configured ?? [];
}

// ---- agents (roadmap #9 Phase 5) -----------------------------------------------
//
// GET /integrations/agents - the caller's agents: cue-cli instances that have
// reported integration runs. Only what an agent reports is shown (name,
// version, when last heard from, and the integrations it runs); spool depth
// and config drift are reported by agents 0.5.0+; an older agent simply omits them.

export interface IntegrationAgent {
  name: string;
  version: string;
  lastSeenAt: number;
  online: boolean;
  mappings: {
    nickname: string;
    title: string;
    lastRunAt: number;
    health: { state: IntegrationHealthState; label: string; reason: string };
    // The Mapping version this agent last ran vs the one published now. `drift`
    // is only true when the agent reported a version and it differs.
    runningVersion?: number;
    activeVersion?: number;
    drift?: boolean;
  }[];
  // Signals the agent holds locally because the backend refused/was unreachable.
  // spoolReported is false for an agent that has never reported it.
  spoolReported?: boolean;
  spoolDepth?: number;
  spoolBytes?: number;
  // Older than AGENT_LATEST_VERSION on the server (never set when unset there).
  outdated?: boolean;
  latestVersion?: string;
}

export async function fetchIntegrationAgents(): Promise<IntegrationAgent[]> {
  const result = await authorizedBackendRequest<{ agents: IntegrationAgent[] }>('/integrations/agents', 'GET');
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load agents.');
  return result.data.agents ?? [];
}

// The first-class Integration read-model (GET /integrations): a mapping
// presented as an Integration - connector, lifecycle, runtime, health, and for
// agent sources which agents run it. Derived server-side on read from the
// mapping (nothing is stored), so it always agrees with GET /mappings.
export interface IntegrationRecord {
  id: string;
  nickname: string;
  name: string;
  connectorId: string;
  connector?: { id: string; name: string; category: string; mode: string; setupKind: 'in_product' | 'agent_required' };
  lifecycle: string;
  runtime: 'cloud_webhook' | 'remote_write' | 'agent_pull' | 'agent_file' | 'agent_host' | '';
  defaultChannel: string;
  credentialRef: boolean;
  format?: string;
  mapping: { nickname: string; activeVersion: number };
  health: { state: IntegrationHealthState; label: string; reason: string };
  assignment?: {
    pinnedAgent?: string;
    agents: { name: string; online: boolean; lastSeenAt: number; runningVersion: number; drift: boolean }[];
  };
}

export async function fetchIntegration(nickname: string): Promise<IntegrationRecord | null> {
  const result = await authorizedBackendRequest<{ integrations: IntegrationRecord[] }>(
    `/integrations?nickname=${encodeURIComponent(nickname)}`,
    'GET',
  );
  if (!result.success || !result.data) throw new Error(result.error ?? 'Unable to load the integration.');
  return result.data.integrations?.[0] ?? null;
}

// ---- Ask Zmist (POST /ask) -------------------------------------------
export type AskClaimKind = 'observation' | 'correlation' | 'hypothesis' | 'recommendation';

export interface AskEvidence {
  channel: string;
  signalType: string;
  from: string;
  to: string;
  value: number | string | null;
  unit: string;
  source: string;
}

export interface AskClaim {
  text: string;
  kind: AskClaimKind;
  evidence: AskEvidence[];
}

export interface AskResponse {
  state: string;
  answer: string;
  claims: AskClaim[];
  limitations: string[];
  asOf: string;
}

const ASK_KINDS: AskClaimKind[] = ['observation', 'correlation', 'hypothesis', 'recommendation'];

// Defensive: unknown claim kinds are downgraded to 'hypothesis' so the UI
// never presents an unlabelled statement as an observation.
export function normalizeAskResponse(raw: any): AskResponse {
  const claims: AskClaim[] = Array.isArray(raw?.claims)
    ? raw.claims.map((c: any) => ({
        text: String(c?.text ?? ''),
        kind: ASK_KINDS.includes(c?.kind) ? c.kind : 'hypothesis',
        evidence: Array.isArray(c?.evidence) ? c.evidence : [],
      }))
    : [];
  return {
    state: String(raw?.state ?? 'done'),
    answer: String(raw?.answer ?? ''),
    claims,
    limitations: Array.isArray(raw?.limitations) ? raw.limitations.map(String) : [],
    asOf: String(raw?.asOf ?? ''),
  };
}

export const askApi = {
  async ask(
    input: { channel?: string; question: string; windowDays?: number },
    signal?: AbortSignal
  ): Promise<AskResponse> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = readCsrfCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${BACKEND_BASE}/ask`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(input),
      signal,
    });
    let parsed: any = null;
    try {
      parsed = await response.json();
    } catch {
      // no JSON body
    }
    const data = parsed?.data;
    if (!response.ok || !data || typeof data !== 'object' || data.error) {
      throw new Error((data && data.error) || `Ask failed (${response.status})`);
    }
    return normalizeAskResponse(data);
  },
};

// The authenticated (cookie + CSRF) request used by the Ask dock's own API
// module (api/assistantApi.ts); exported rather than duplicated so there is
// one place that knows how a signed-in call is made.
export const assistantRequest = authorizedBackendRequest;
